import React, { Fragment, useState, useEffect } from 'react';
import { getAdvAuthRdnLicReportService } from '../../../services/BOEReportService';
import { getReferenceDataService } from '../../../services/MasterService';
import { getAllAdminIECsService } from '../../../services/FileDataService';
import { Table, Button, Spin, message, Select, Tabs, Dropdown, Menu } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { Col, Row } from 'react-bootstrap';
import Card from '../../../components/bootstrap/card';
import { getTodaysDate, sortBySrNo } from '../../../utilities/utils';
import ExcelJS from 'exceljs';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import dayjs from 'dayjs';
import useColumnVisibility from '../../../hooks/useColumnVisibility';
import ColumnSelector from '../../../components/common/ColumnSelector';

const getLines = (text, width) => {
    if (text === undefined || text === null) return 1;
    const paragraphs = String(text).split('\n');
    let lines = 0;
    paragraphs.forEach(p => {
        lines += Math.ceil(p.length / width) || 1;
    });
    return lines;
};

// Reference signature blocks spell out the full company name without the
// trailing " -ACRONYM" trade suffix (e.g. "RIVAAN TECHNOLOGIES PRIVATE LIMITED"),
// even though the main banner line keeps it ("... -RTPL"). Strip it only where
// the reference does — the signature "FOR ..." lines.
const stripTradeSuffix = (name) => String(name || '').replace(/\s+-\S+$/, '').trim();

const { TabPane } = Tabs;

// ─── FIX: Safe helper to set value on a merged cell in ExcelJS ───────────────
// ExcelJS can silently drop values if the cell object was obtained before merge.
// Always call mergeCells first, then get a FRESH reference via ws.getCell().
const setMergedCell = (ws, range, value, opts = {}) => {
    const { bold = false, horizontal = 'center', wrapText = false, fontSize = 10, fill = null } = opts;
    const parts = range.split(':');
    const isMerge = parts.length === 2 && parts[0] !== parts[1];
    if (isMerge) {
        try { ws.mergeCells(range); } catch (e) { /* already merged – ignore */ }
    }
    // Always get a FRESH cell reference AFTER the merge call
    const cell = ws.getCell(parts[0]);
    cell.value = value;
    cell.font = { bold, size: fontSize };
    cell.alignment = { horizontal, vertical: 'middle', wrapText };
    if (fill) cell.fill = fill;
    return cell;
};

const applyOuterBorder = (ws, startRow, endRow, startCol, endCol, style = 'thin') => {
    for (let r = startRow; r <= endRow; r++) {
        for (let c = startCol; c <= endCol; c++) {
            const cell = ws.getCell(r, c);
            const border = cell.border ? { ...cell.border } : {};
            if (r === startRow) border.top = { style };
            if (r === endRow) border.bottom = { style };
            if (c === startCol) border.left = { style };
            if (c === endCol) border.right = { style };
            cell.border = border;
        }
    }
};

// ExcelJS's `sheet.autoFilter = range` only writes the worksheet-level
// <autoFilter> element. Excel (and LibreOffice) also need a companion
// workbook-level hidden defined name, `_xlnm._FilterDatabase`, to actually
// register the filter and render the dropdown buttons — without it the
// range is present in the file but the buttons silently don't show. ExcelJS
// has no public API for this reserved name, so this post-processes the
// already-written buffer's xl/workbook.xml directly.
const addFilterDatabaseName = async (buffer, sheetName, range) => {
    const zip = await JSZip.loadAsync(buffer);
    const path = 'xl/workbook.xml';
    const xml = await zip.file(path).async('string');
    const escapedSheetName = sheetName.replace(/'/g, "''");
    const definedNameEntry = `<definedName hidden="1" localSheetId="0" name="_xlnm._FilterDatabase">'${escapedSheetName}'!${range}</definedName>`;
    // ExcelJS never writes a <definedNames> block on its own, but guard for it
    // anyway in case that changes in a future version.
    const patched = xml.includes('<definedNames>')
        ? xml.replace('<definedNames>', `<definedNames>${definedNameEntry}`)
        : xml.replace('</sheets>', `</sheets><definedNames>${definedNameEntry}</definedNames>`);
    zip.file(path, patched);
    return zip.generateAsync({ type: 'blob', mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
};

const AdvanceAuthRdnLicReport = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('1');
    const [IECOptions, setIECOptions] = useState([]);
    const userRole = localStorage.getItem('role');
    const [selectedIEC, setSelectedIEC] = useState(localStorage.getItem('iec_no'));

    // ─── FIX: Ensure selectedIECName is never null ────────────────────────────
    // localStorage.getItem returns null when key is absent; coerce to string early.
    const [selectedIECName, setSelectedIECName] = useState(localStorage.getItem('name') || '');

    const [billNo, setBillNo] = useState("");
    const [billNoOptions, setBillNoOptions] = useState([]);

    // Data states for the 6 tabs
    const [exportLedgerData, setExportLedgerData] = useState([]);
    const [importLedgerData, setImportLedgerData] = useState([]);
    const [importStData, setImportStData] = useState([]);
    const [exportStData, setExportStData] = useState([]);
    const [shbWiseData, setShbWiseData] = useState([]);
    const [masterSummaryData, setMasterSummaryData] = useState([]);

    const processExportLedger = (shbData, importData, exportData) => {
        if (!shbData || shbData.length === 0) return [];

        const importMap = new Map();
        (importData || []).forEach(row => {
            const key = String(row.import_sr_no);
            if (!importMap.has(key)) {
                importMap.set(key, {
                    cif_imported_inr: 0,
                    bcd_rate: row.bcd_rate != null ? Number(row.bcd_rate) : 15,
                    sws_rate: row.sws_rate != null ? Number(row.sws_rate) : 10,
                    igst_rate: row.igst_rate != null ? Number(row.igst_rate) : 18,
                });
            }
            const item = importMap.get(key);
            item.cif_imported_inr += Number(row.cif_imported_inr) || 0;
            if (row.bcd_rate != null) item.bcd_rate = Number(row.bcd_rate);
            if (row.sws_rate != null) item.sws_rate = Number(row.sws_rate);
            if (row.igst_rate != null) item.igst_rate = Number(row.igst_rate);
        });

        const exportMap = new Map();
        (exportData || []).forEach(row => {
            const key = String(row.export_sr_no);
            if (!exportMap.has(key)) {
                exportMap.set(key, {
                    actual_export_qty: 0,
                    fob_fc: 0,
                    realised_fc: 0,
                });
            }
            const item = exportMap.get(key);
            item.actual_export_qty += Number(row.export_qty) || 0;
            // "Direct Export - FOB in FC" isn't a true foreign-currency figure for INR-currency
            // rows, so exclude them here too — matches the Export St sheet's own total-row logic.
            if (String(row.currency).trim().toUpperCase() !== 'INR') {
                item.fob_fc += Number(row.fob_fc) || 0;
            }
            item.realised_fc += Number(row.net_realised_value_fc != null ? row.net_realised_value_fc : row.total_realised_value_fc) || 0;
        });

        const groups = new Map();
        shbData.forEach(row => {
            const expSr = row.exp_sr_no != null ? String(row.exp_sr_no) : '1';
            const impSr = row.import_item_sr_no != null ? String(row.import_item_sr_no) : '1';
            const groupKey = `${expSr}_${impSr}`;

            if (!groups.has(groupKey)) {
                groups.set(groupKey, {
                    exp_sr_no: Number(expSr) || 1,
                    exp_qty_as_per_auth: Number(row.exp_qty_as_per_auth) || 0,
                    actual_export_qty: 0,
                    import_item_sr_no: Number(impSr) || 1,
                    imp_qty_as_per_lic: Number(row.imp_qty_as_per_lic) || 0,
                    actual_imports_made: 0,
                });
            }
            const g = groups.get(groupKey);
            // The SHB view repeats the import-serial total on every export item.
            // It is a group-level value, so retain it once instead of summing it.
            g.actual_imports_made = Math.max(
                g.actual_imports_made,
                Number(row.actual_imports_made) || 0
            );
            g.actual_export_qty += Number(row.actual_export_qty) || 0;
        });

        const result = Array.from(groups.values()).map(g => {
            const expKey = String(g.exp_sr_no);
            const impKey = String(g.import_item_sr_no);

            const expInfo = exportMap.get(expKey) || {};
            const impInfo = importMap.get(impKey) || {};

            const actualExportQty = g.actual_export_qty || expInfo.actual_export_qty || 0;
            const actualImportQty = g.actual_imports_made || 0;
            const actualCifInr = impInfo.cif_imported_inr || 0;

            const bcdRate = impInfo.bcd_rate != null ? impInfo.bcd_rate : 15;
            const swsRate = impInfo.sws_rate != null ? impInfo.sws_rate : 10;
            const igstRate = impInfo.igst_rate != null ? impInfo.igst_rate : 18;

            const actualExportFobFc = expInfo.fob_fc || 0;
            const realisationFc = expInfo.realised_fc || 0;

            const propExpQty = g.imp_qty_as_per_lic > 0 ? (g.exp_qty_as_per_auth / g.imp_qty_as_per_lic) * actualImportQty : 0;
            const expBalQty = propExpQty - actualExportQty;
            const impQtyReqBal = g.exp_qty_as_per_auth > 0 ? (g.imp_qty_as_per_lic / g.exp_qty_as_per_auth) * expBalQty : 0;
            const impCifReqBal = actualImportQty > 0 ? (actualCifInr / actualImportQty) * impQtyReqBal : 0;

            const bcdExempted = actualCifInr * (bcdRate / 100);
            const swsExempted = bcdExempted * (swsRate / 100);
            const igstExempted = (actualCifInr + bcdExempted + swsExempted) * (igstRate / 100);
            const totalDutyExempted = bcdExempted + swsExempted + igstExempted;

            const bcdPayableExcess = actualImportQty > 0 ? (bcdExempted / actualImportQty) * impQtyReqBal : 0;
            const swsPayableExcess = actualImportQty > 0 ? (swsExempted / actualImportQty) * impQtyReqBal : 0;
            const igstPayableExcess = actualImportQty > 0 ? (igstExempted / actualImportQty) * impQtyReqBal : 0;
            const totalDutyBalExp = bcdPayableExcess + swsPayableExcess + igstPayableExcess;

            return {
                export_sr_no: g.exp_sr_no,
                exp_qty_as_per_auth: g.exp_qty_as_per_auth,
                actual_export_qty: actualExportQty,
                import_item_sr_no: g.import_item_sr_no,
                imp_qty_as_per_lic: g.imp_qty_as_per_lic,
                actual_imports_made: actualImportQty,
                actual_cif_inr: actualCifInr,
                prop_exp_qty: propExpQty,
                exp_bal_qty: expBalQty,
                imp_qty_req_bal: impQtyReqBal,
                imp_cif_req_bal: impCifReqBal,
                bcd_rate: bcdRate,
                sws_rate: swsRate,
                igst_rate: igstRate,
                bcd_exempted: bcdExempted,
                sws_exempted: swsExempted,
                igst_exempted: igstExempted,
                total_duty_exempted: totalDutyExempted,
                bcd_payable_excess: bcdPayableExcess,
                sws_payable_excess: swsPayableExcess,
                igst_payable_excess: igstPayableExcess,
                total_duty_bal_exp: totalDutyBalExp,
                actual_export_fob_fc: actualExportFobFc,
                realisation_fc: realisationFc,
            };
        });

        return result.sort((a, b) => {
            if (a.export_sr_no !== b.export_sr_no) return a.export_sr_no - b.export_sr_no;
            return a.import_item_sr_no - b.import_item_sr_no;
        });
    };

    const processImportLedger = (importData) => {
        if (!importData || importData.length === 0) return [];
        const groups = new Map();
        importData.forEach(row => {
            const srNo = row.import_sr_no != null ? String(row.import_sr_no) : '__null__';
            if (!groups.has(srNo)) {
                groups.set(srNo, {
                    import_sr_no: row.import_sr_no,
                    qty_allowed: row.qty_allowed != null ? Number(row.qty_allowed) : 0,
                    item_imported: row.item_imported || '',
                    cif_allowed_inr: row.cif_allowed_inr != null ? Number(row.cif_allowed_inr) : 0,
                    cif_allowed_usd: row.cif_allowed_usd != null ? Number(row.cif_allowed_usd) : 0,
                    qty_imported: 0,
                    cif_imported_inr: 0,
                    cif_imported_usd: 0,
                    debit_value_fcc: 0,
                });
            }
            const g = groups.get(srNo);
            g.qty_imported += Number(row.qty_imported) || 0;
            g.cif_imported_inr += Number(row.cif_imported_inr) || 0;
            g.cif_imported_usd += Number(row.cif_imported_usd) || 0;
            g.debit_value_fcc += Number(row.debit_value_fcc != null ? row.debit_value_fcc : row.cif_imported_usd) || 0;
        });

        const result = Array.from(groups.values()).map(g => {
            const import_bal_qty = g.qty_allowed - g.qty_imported;
            const import_bal_cif_inr = g.cif_allowed_inr - g.cif_imported_inr;
            return {
                ...g,
                import_bal_qty: import_bal_qty,
                import_bal_cif_inr: import_bal_cif_inr,
            };
        });

        return result.sort((a, b) => (Number(a.import_sr_no) || 0) - (Number(b.import_sr_no) || 0));
    };

    const today = new Date();
    const formattedDate = `${String(today.getDate()).padStart(2, '0')}-${String(today.getMonth() + 1).padStart(2, '0')}-${today.getFullYear()}`;

    // ─── TABLE STYLES ────────────────────────────────────────────────────────────
    const tableStyles = `
        .rdn-export-ledger-table .ant-table-thead > tr > th,
        .rdn-import-ledger-table .ant-table-thead > tr > th,
        .rdn-import-table .ant-table-thead > tr > th,
        .rdn-export-table .ant-table-thead > tr > th,
        .rdn-shb-table .ant-table-thead > tr > th,
        .rdn-master-table .ant-table-thead > tr > th {
            white-space: normal !important;
            word-break: break-word !important;
            overflow: visible !important;
            text-overflow: unset !important;
            vertical-align: middle !important;
            line-height: 1.3 !important;
            padding-top: 8px !important;
            padding-bottom: 8px !important;
        }
        .rdn-export-ledger-table .ant-table-tbody > tr > td,
        .rdn-import-ledger-table .ant-table-tbody > tr > td,
        .rdn-import-table .ant-table-tbody > tr > td,
        .rdn-export-table .ant-table-tbody > tr > td,
        .rdn-shb-table .ant-table-tbody > tr > td,
        .rdn-master-table .ant-table-tbody > tr > td {
            white-space: nowrap;
        }
        .rdn-export-ledger-table .ant-table-container,
        .rdn-import-ledger-table .ant-table-container,
        .rdn-import-table .ant-table-container,
        .rdn-export-table .ant-table-container,
        .rdn-shb-table .ant-table-container,
        .rdn-master-table .ant-table-container {
            overflow-x: auto !important;
        }
        .rdn-export-ledger-table .ant-table-header,
        .rdn-import-ledger-table .ant-table-header,
        .rdn-import-table .ant-table-header,
        .rdn-export-table .ant-table-header,
        .rdn-shb-table .ant-table-header,
        .rdn-master-table .ant-table-header {
            overflow: hidden !important;
        }
        .rdn-export-ledger-table .ant-table-sticky-holder,
        .rdn-import-ledger-table .ant-table-sticky-holder,
        .rdn-import-table .ant-table-sticky-holder,
        .rdn-export-table .ant-table-sticky-holder,
        .rdn-shb-table .ant-table-sticky-holder,
        .rdn-master-table .ant-table-sticky-holder {
            overflow: hidden !important;
        }
    `;

    const exportLedgerColumns = [
        { title: 'Export Sr No', dataIndex: 'export_sr_no', key: 'export_sr_no', width: 90, align: 'center' },
        { title: 'Export Qty As per AA', dataIndex: 'exp_qty_as_per_auth', key: 'exp_qty_as_per_auth', width: 140, align: 'right', render: (val) => val != null ? Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-' },
        { title: 'Actual Export Qty', dataIndex: 'actual_export_qty', key: 'actual_export_qty', width: 140, align: 'right', render: (val) => val != null ? Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-' },
        { title: 'Import Sr No', dataIndex: 'import_item_sr_no', key: 'import_item_sr_no', width: 90, align: 'center' },
        { title: 'Import Qty As per AA', dataIndex: 'imp_qty_as_per_lic', key: 'imp_qty_as_per_lic', width: 140, align: 'right', render: (val) => val != null ? Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-' },
        { title: 'Actual Import Qty', dataIndex: 'actual_imports_made', key: 'actual_imports_made', width: 140, align: 'right', render: (val) => (val && val !== 0) ? Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-' },
        { title: 'Actual CIF Value (in INR)', dataIndex: 'actual_cif_inr', key: 'actual_cif_inr', width: 170, align: 'right', render: (val) => (val && val !== 0) ? Number(val).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-' },
        { title: 'Export Qty Prop. of Actual Import', dataIndex: 'prop_exp_qty', key: 'prop_exp_qty', width: 170, align: 'right', render: (val) => val != null ? Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-' },
        { title: 'Export Balance Quantity', dataIndex: 'exp_bal_qty', key: 'exp_bal_qty', width: 150, align: 'right', render: (val) => val != null ? Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-' },
        { title: 'Import Qty req. for Bal Export', dataIndex: 'imp_qty_req_bal', key: 'imp_qty_req_bal', width: 170, align: 'right', render: (val) => val != null ? Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-' },
        { title: 'Import CIF Value req. for Bal Export', dataIndex: 'imp_cif_req_bal', key: 'imp_cif_req_bal', width: 180, align: 'right', render: (val) => val != null ? Number(val).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-' },
        { title: 'Assessable Value', dataIndex: 'actual_cif_inr', key: 'assessable_val', width: 150, align: 'right', render: (val) => (val && val !== 0) ? Number(val).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-' },
        { title: 'BCD % Duty Rate', dataIndex: 'bcd_rate', key: 'bcd_rate', width: 110, align: 'right', render: (val) => val != null ? `${val}%` : '-' },
        { title: 'SWS % Duty Rate', dataIndex: 'sws_rate', key: 'sws_rate', width: 110, align: 'right', render: (val) => val != null ? `${val}%` : '-' },
        { title: 'IGST % Duty Rate', dataIndex: 'igst_rate', key: 'igst_rate', width: 110, align: 'right', render: (val) => val != null ? `${val}%` : '-' },
        { title: 'BCD Exempted', dataIndex: 'bcd_exempted', key: 'bcd_exempted', width: 140, align: 'right', render: (val) => val != null ? Number(val).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-' },
        { title: 'SWS Exempted', dataIndex: 'sws_exempted', key: 'sws_exempted', width: 140, align: 'right', render: (val) => val != null ? Number(val).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-' },
        { title: 'IGST Exempted', dataIndex: 'igst_exempted', key: 'igst_exempted', width: 140, align: 'right', render: (val) => val != null ? Number(val).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-' },
        { title: 'Total Duty Exempted', dataIndex: 'total_duty_exempted', key: 'total_duty_exempted', width: 160, align: 'right', render: (val) => val != null ? Number(val).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-' },
        { title: 'BCD Payable on Excess', dataIndex: 'bcd_payable_excess', key: 'bcd_payable_excess', width: 170, align: 'right', render: (val) => val != null ? Number(val).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-' },
        { title: 'SWS Payable on Excess', dataIndex: 'sws_payable_excess', key: 'sws_payable_excess', width: 170, align: 'right', render: (val) => val != null ? Number(val).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-' },
        { title: 'IGST Payable on Excess', dataIndex: 'igst_payable_excess', key: 'igst_payable_excess', width: 170, align: 'right', render: (val) => val != null ? Number(val).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-' },
        { title: 'Total Duty on Bal Export', dataIndex: 'total_duty_bal_exp', key: 'total_duty_bal_exp', width: 180, align: 'right', render: (val) => val != null ? Number(val).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-' },
        { title: 'Actual Export FOB in FCC', dataIndex: 'actual_export_fob_fc', key: 'actual_export_fob_fc', width: 170, align: 'right', render: (val) => val ? Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-' },
        { title: 'Realisation in FCC', dataIndex: 'realisation_fc', key: 'realisation_fc', width: 170, align: 'right', render: (val) => val ? Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-' }
    ];

    const importLedgerColumns = [
        {
            title: 'Import Sr No',
            dataIndex: 'import_sr_no',
            key: 'import_sr_no',
            width: 100,
            align: 'center',
            sorter: (a, b) => (Number(a.import_sr_no) || 0) - (Number(b.import_sr_no) || 0),
        },
        {
            title: 'Import Qty As per AA',
            dataIndex: 'qty_allowed',
            key: 'qty_allowed',
            width: 160,
            align: 'right',
            render: (val) => val != null ? Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-',
        },
        {
            title: 'Import Description',
            dataIndex: 'item_imported',
            key: 'item_imported',
            width: 300,
        },
        {
            title: 'CIF Value (As per AA)',
            dataIndex: 'cif_allowed_inr',
            key: 'cif_allowed_inr',
            width: 180,
            align: 'right',
            render: (val) => val != null ? Number(val).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-',
        },
        {
            title: 'CIF FCC (As per AA)',
            dataIndex: 'cif_allowed_usd',
            key: 'cif_allowed_usd',
            width: 160,
            align: 'right',
            render: (val) => val != null ? Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-',
        },
        {
            title: 'Actual Import Qty',
            dataIndex: 'qty_imported',
            key: 'qty_imported',
            width: 160,
            align: 'right',
            render: (val) => (val && val !== 0) ? Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-',
        },
        {
            title: 'Actual CIF Value (As per BOE)',
            dataIndex: 'cif_imported_inr',
            key: 'cif_imported_inr',
            width: 210,
            align: 'right',
            render: (val) => (val && val !== 0) ? Number(val).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-',
        },
        {
            title: 'Actual CIF FCC (As per BOE)',
            dataIndex: 'debit_value_fcc',
            key: 'debit_value_fcc',
            width: 190,
            align: 'right',
            render: (val, record) => {
                const displayVal = (val && val !== 0) ? val : record?.cif_imported_usd;
                return (displayVal && displayVal !== 0) ? Number(displayVal).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-';
            },
        },
        {
            title: 'Import Bal Qty',
            dataIndex: 'import_bal_qty',
            key: 'import_bal_qty',
            width: 160,
            align: 'right',
            render: (val) => val != null ? Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-',
        },
        {
            title: 'Bal Qty CIF in Rs',
            dataIndex: 'import_bal_cif_inr',
            key: 'import_bal_cif_inr',
            width: 180,
            align: 'right',
            render: (val) => val != null ? Number(val).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-',
        },
    ];

    // Generate dynamic columns based on data keys
    const generateColumns = (data, currency) => {
        if (!data || data.length === 0) return [];
        return Object.keys(data[0]).map(key => {
            let title = key.toUpperCase().replace(/_/g, ' ');
            // Name the licence's currency in the "…USD" value columns (e.g. an EUR licence).
            if (currency) title = title.replace(/\bUSD\b/g, currency);
            const excelWidth = Math.min(45, Math.max(14, title.length + 6));
            const pixelWidth = (title.length * 9) + 32;
            return {
                title: title,
                dataIndex: key,
                key: key,
                header: title,
                width: pixelWidth,
                excelWidth: excelWidth
            };
        });
    };

    // Licence currency for the "value in licence currency" column headers, so the UI
    // tables name EUR/GBP/etc. per the licence instead of a hardcoded "USD".
    const uiLicData = (masterSummaryData || []).find(
        item => String(item.lic_no || '').trim() === String(billNo || '').trim()
    ) || (masterSummaryData || [])[0] || {};
    const uiLicCurrency = String(uiLicData.cif_currency || uiLicData.eo_currency || '').trim().toUpperCase() || 'USD';

    const importStColumns = generateColumns(importStData, uiLicCurrency);

    const exportStColumns = [
        { title: 'EXPORT SR NO', dataIndex: 'export_sr_no', key: 'export_sr_no', width: 120 },
        { title: 'PRODUCT EXPORTED', dataIndex: 'product_exported', key: 'product_exported', width: 250 },
        { title: 'ITC HS CODE', dataIndex: 'itc_hs_code', key: 'itc_hs_code', width: 120 },
        { title: 'SHIPPING BILL NO', dataIndex: 'shipping_bill_no', key: 'shipping_bill_no', width: 140 },
        { title: 'SHIPPING BILL DATE', dataIndex: 'shipping_bill_date', key: 'shipping_bill_date', width: 140, render: (val) => val ? dayjs(val).format('DD-MMM-YY') : '' },
        { title: 'EXPORT QTY', dataIndex: 'export_qty', key: 'export_qty', width: 120, align: 'right', render: (val) => val ? Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-' },
        { title: 'UOM', dataIndex: 'uom', key: 'uom', width: 80 },
        { title: 'CURRENCY', dataIndex: 'currency', key: 'currency', width: 100 },
        { title: 'SHR EXCHG RATE', dataIndex: 'shb_exchange_rate', key: 'shb_exchange_rate', width: 120, align: 'right', render: (val) => val ? Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 }) : '-' },
        { title: 'Direct Export - FOB in INR', dataIndex: 'fob_inr', key: 'fob_inr', width: 180, align: 'right', render: (val) => val ? Number(val).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-' },
        { title: 'Direct Export - FOB in FC', dataIndex: 'fob_fc', key: 'fob_fc', width: 180, align: 'right', render: (val, record) => { const fcVal = val ?? (record.fob_inr / record.shb_exchange_rate); return fcVal ? Number(fcVal).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'; } },
        { title: `Direct Export - FOB in ${uiLicCurrency}`, dataIndex: 'fob_usd', key: 'fob_usd', width: 180, align: 'right', render: (_, record) => { const fobInr = Number(record.fob_inr), usdRate = Number(record.usd_rate); return (fobInr && usdRate) ? (fobInr / usdRate).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'; } },
        { title: 'TOTAL REALISED VALUE IN INR', dataIndex: 'total_realised_value_inr', key: 'total_realised_value_inr', width: 180, align: 'right', render: (val) => val ? Number(val).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-' },
        { title: `TOTAL REALISED VALUE IN ${uiLicCurrency}`, dataIndex: 'total_realised_value_usd', key: 'total_realised_value_usd', width: 180, align: 'right', render: (val) => val ? Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-' },
        { title: 'REFERENCE CODE', dataIndex: 'reference_code', key: 'ref_code', width: 180, render: (_, record) => record.reference_code || record.ref_code || (record.shipping_bill_no ? `${record.shipping_bill_no}-${record.export_sr_no || 1}-1` : '') },
        { title: 'Total FOB (INR)', dataIndex: 'shb_total_fob_inr', key: 'shb_total_fob_inr', width: 150, align: 'right', render: (val) => val ? Number(val).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-' },
        { title: 'TOTAL REALISED VALUE FC', dataIndex: 'shb_realised_value_fc', key: 'shb_realised_value_fc', width: 180, align: 'right', render: (val) => val ? Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-' },
        { title: 'FREIGHT VALUE FC', dataIndex: 'shb_freight_fc', key: 'shb_freight_fc', width: 150, align: 'right', render: (val) => val ? Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-' },
        { title: 'INSURANCE VAL FC', dataIndex: 'shb_insurance_fc', key: 'shb_insurance_fc', width: 150, align: 'right', render: (val) => val ? Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-' },
        { title: 'NET REALISED VALUE FC', dataIndex: 'shb_net_realised_fc', key: 'shb_net_realised_fc', width: 180, align: 'right', render: (val) => val ? Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-' },
        { title: 'BRC Amount Excluding Insurance & Freight in FC', dataIndex: 'net_realised_value_fc', key: 'net_realised_value_fc', width: 250, align: 'right', render: (val) => val ? Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-' },
        { title: 'As per license currency ex rate as per Shipping Bill date', dataIndex: 'usd_rate', key: 'usd_rate', width: 200, align: 'right', render: (val) => val ? Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 }) : '-' },
        { title: 'BRC Lot', dataIndex: 'brc_lot', key: 'brc_lot', width: 120, align: 'center', render: (val) => val || '-' }
    ];

    const shbWiseColumns = generateColumns(shbWiseData);
    const masterSummaryColumns = [
        { title: 'LIC NO', dataIndex: 'lic_no', key: 'lic_no', width: 120 },
        { title: 'LIC DATE', dataIndex: 'lic_date', key: 'lic_date', width: 120, render: (val) => val ? dayjs(val).format('DD-MMM-YY') : '' },
        { title: 'FILE NO', dataIndex: 'file_no', key: 'file_no', width: 180 },
        { title: 'CIF ALLOWED INR', dataIndex: 'cif_allowed_inr', key: 'cif_allowed_inr', width: 150, align: 'right', render: (val) => val ? Number(val).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-' },
        { title: 'CIF ALLOWED USD', dataIndex: 'cif_allowed_usd', key: 'cif_allowed_usd', width: 150, align: 'right', render: (val) => val ? Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-' },
        { title: 'EXPORT OBLIGATION INR', dataIndex: 'export_obligation_inr', key: 'export_obligation_inr', width: 180, align: 'right', render: (val) => val ? Number(val).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-' },
        { title: 'EXPORT OBLIGATION USD', dataIndex: 'export_obligation_usd', key: 'export_obligation_usd', width: 180, align: 'right', render: (val) => val ? Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-' },
        { title: 'EOP DATE', dataIndex: 'eop_date', key: 'eop_date', width: 120, render: (val) => val ? dayjs(val).format('DD-MMM-YY') : '' },
        { title: 'Type of Norm', dataIndex: 'type_of_norm', key: 'type_of_norm', width: 150 },
        { title: 'CIF Currency', dataIndex: 'cif_currency', key: 'cif_currency', width: 100 },
        { title: 'EO Currency', dataIndex: 'eo_currency', key: 'eo_currency', width: 100 },
        { title: 'Add', dataIndex: 'address', key: 'address', width: 250 },
        { title: 'Name of Company', dataIndex: 'company_name', key: 'company_name', width: 250 },
        { title: 'REGIONAL OFFICE', dataIndex: 'dgft_regional_office', key: 'dgft_regional_office', width: 180 },
        { title: 'IEC NO', dataIndex: 'iec_no', key: 'iec_no', width: 120 },
        { title: 'TOTAL ACTUAL EXPORT INR', dataIndex: 'total_actual_export_inr', key: 'total_actual_export_inr', width: 180, align: 'right', render: (val) => val ? Number(val).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-' },
        { title: 'TOTAL ACTUAL EXPORT USD', dataIndex: 'total_actual_export_usd', key: 'total_actual_export_usd', width: 180, align: 'right', render: (val) => val ? Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-' },
        { title: 'TOTAL ACTUAL IMPORT INR', dataIndex: 'total_actual_import_inr', key: 'total_actual_import_inr', width: 180, align: 'right', render: (val) => val ? Number(val).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-' },
        { title: 'TOTAL ACTUAL IMPORT USD', dataIndex: 'total_actual_import_usd', key: 'total_actual_import_usd', width: 180, align: 'right', render: (val) => val ? Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-' },
        { title: 'VALUE ADDITION PERCENT', dataIndex: 'value_addition_percent', key: 'value_addition_percent', width: 180, align: 'right', render: (val) => val ? `${Number(val).toFixed(2)}%` : '-' }
    ];

    // Per-tab column selection. Each tab keeps its own selection (persisted
    // per-user in localStorage). The Export St key is kept as the original
    // 'AdvanceAuthRdnLicReport' so previously saved selections carry over.
    const importStVisibility = useColumnVisibility('AdvanceAuthRdnLicImportSt', importStColumns);
    const exportStVisibility = useColumnVisibility('AdvanceAuthRdnLicReport', exportStColumns);
    const shbWiseVisibility = useColumnVisibility('AdvanceAuthRdnLicShbWise', shbWiseColumns);
    const masterSummaryVisibility = useColumnVisibility('AdvanceAuthRdnLicMasterSummary', masterSummaryColumns);
    const importLedgerVisibility = useColumnVisibility('AdvanceAuthRdnLicImportLedger', importLedgerColumns);
    const exportLedgerVisibility = useColumnVisibility('AdvanceAuthRdnLicExportLedger', exportLedgerColumns);

    // Map the active tab to the column set the ColumnSelector should control.
    const tabColumnConfig = {
        '1': { allColumns: importStColumns, ...importStVisibility },
        '2': { allColumns: exportStColumns, ...exportStVisibility },
        '3': { allColumns: shbWiseColumns, ...shbWiseVisibility },
        '4': { allColumns: masterSummaryColumns, ...masterSummaryVisibility },
        '5': { allColumns: importLedgerColumns, ...importLedgerVisibility },
        '6': { allColumns: exportLedgerColumns, ...exportLedgerVisibility },
    };
    const activeColumnConfig = tabColumnConfig[activeTab] || tabColumnConfig['1'];

    const fetchData = async () => {
        const filters = {
            bill_no: billNo || null,
        };
        try {
            setLoading(true);
            setError(null);
            const response = await getAdvAuthRdnLicReportService(selectedIEC, filters);

            if (response) {
                // The API orders imports by serial number and BOE date.
                const importRows = response.import_st || [];
                setImportStData(importRows);
                setImportLedgerData(processImportLedger(importRows));

                // The API orders by licence Export Sr No, then by the existing
                // shipping-bill/invoice/item sequence. Keep that single order
                // for the table, Excel statement and ledger calculations.
                setExportStData(response.export_st || []);

                // The API returns SHB rows in Export Statement/reference-code order.
                const shbWiseRows = response.shb_wise || [];
                setShbWiseData(shbWiseRows);
                setExportLedgerData(processExportLedger(shbWiseRows, importRows, response.export_st));

                setMasterSummaryData(response.master_summary || []);
            }
        } catch (error) {
            setError(error.message);
            console.error('Error fetching data:', error);
            message.error(`Error fetching data: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const fetchLicenseNumbers = async () => {
        if (!selectedIEC) return;
        try {
            const data = await getReferenceDataService(selectedIEC, 'ADV_AUTH');
            const options = data.map((item, index) => ({
                value: item.license_number,
                label: item.license_number,
                key: index,
            }));
            setBillNoOptions(options);
        } catch (error) {
            console.error('Error fetching license numbers:', error);
            message.error('Failed to load license numbers');
        }
    };

    const getAdminIEC = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await getAllAdminIECsService(token);
            if (response?.data) {
                const options = response.data.map((item, index) => ({
                    value: `${item.iec_no};${item.fullname}`,
                    label: `${item.iec_no} (${item.fullname})`,
                    key: index,
                }));
                setIECOptions(options);
            }
        } catch (err) {
            message.error("Failed to load IEC list");
        }
    };

    useEffect(() => {
        if (userRole === 'superadmin') {
            getAdminIEC();
        }
        if (userRole === 'admin' || userRole === 'user') {
            fetchLicenseNumbers();
        }
    }, []);

    useEffect(() => {
        if (selectedIEC) {
            fetchLicenseNumbers();
        }
    }, [selectedIEC]);

    const handleIECSelection = (val) => {
        if (val) {
            const [iec_no, iec_name] = val.split(";");
            setSelectedIEC(iec_no);
            setSelectedIECName(iec_name || '');
        } else {
            setSelectedIEC(null);
            setSelectedIECName('');
        }
        setBillNo('');
        setExportLedgerData([]);
        setImportLedgerData([]);
        setImportStData([]);
        setExportStData([]);
        setShbWiseData([]);
        setMasterSummaryData([]);
    };

    const handleSubmit = () => {
        if (!selectedIEC && userRole === 'superadmin') {
            message.error("Please select an IEC");
            return;
        }
        if (!billNo) {
            message.error("Please select an Authentication Number");
            return;
        }
        fetchData();
    };

    const handleBillNoSelection = (val) => {
        setBillNo(val || "");
    };

    // ─── SHARED: build header rows (Name / Lic No / File No) for any sheet ───
    // Header format matches the template image:
    //   Row 1: NAME OF COMPANY:- {companyName}
    //   Row 2: ADVANCE LIC NO. {licNo} Date. {licDate}
    //   Row 3: FILE NO:- {fileNo}
    //   Row 4: blank spacer
    //   Row 5: section title (EXPORT STATEMENT / IMPORT STATEMENT etc.)
    const writeSheetHeader = (ws, { startColLetter, lastColLetter, headerLastColLetter, bannerLastColLetter, companyName, licNo, licDate, fileNo, titleText }) => {
        const sCol = startColLetter || 'B';
        // headerLastColLetter: merge limit for the row-5 title band only (defaults to lastColLetter)
        const hLast = headerLastColLetter || lastColLetter;
        // bannerLastColLetter: merge limit for rows 1-3 (company/lic/file) — the reference never
        // merges these across the full table width, it leaves them as a single cell at sCol that
        // visually overflows across the empty cells to its right. Defaults to sCol (no merge).
        const bLast = bannerLastColLetter || sCol;

        // Rows 1-4 share a fixed height across every sheet that uses this header.
        for (let r = 1; r <= 4; r++) ws.getRow(r).height = 15.75;

        // Row 1 — Column A is an empty spacer; content starts at startCol (default B)
        setMergedCell(ws, `${sCol}1:${bLast}1`,
            `NAME OF COMPANY:- ${companyName ? String(companyName).trim() : ''}`,
            { bold: true, horizontal: 'left', fontSize: 12 });

        // Row 2
        const licDisplay = licNo
            ? `ADVANCE LIC NO. ${String(licNo).trim()}${licDate ? `  Date. ${String(licDate).trim()}` : ''}`
            : `ADVANCE LIC NO. ${licDate ? `Date. ${String(licDate).trim()}` : ''}`;
        setMergedCell(ws, `${sCol}2:${bLast}2`, licDisplay.trim(), { bold: true, horizontal: 'left', fontSize: 12 });

        // Row 3
        setMergedCell(ws, `${sCol}3:${bLast}3`,
            `FILE NO:- ${fileNo ? String(fileNo).trim() : ''}`,
            { bold: true, horizontal: 'left', fontSize: 12 });

        // Row 4: blank spacer (reference leaves this at default row height)

        // Row 5: section title (only when titleText is provided; otherwise leave as a blank spacer, like the reference)
        if (titleText) {
            ws.getRow(5).height = 21;
            setMergedCell(ws, `${sCol}5:${hLast}5`, titleText, { bold: true, fontSize: 12 });
            // Reference boxes this title in a light-grey band across the full merge range,
            // not just the anchor cell — apply fill/border to every column in [sCol, hLast].
            const grey = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF2F2F2' } };
            const thin = { style: 'thin' };
            const titleBorders = { top: thin, left: thin, bottom: thin, right: thin };
            for (let code = sCol.charCodeAt(0); code <= hLast.charCodeAt(0); code++) {
                const cell = ws.getCell(`${String.fromCharCode(code)}5`);
                cell.fill = grey;
                cell.border = titleBorders;
                cell.font = { bold: true, size: 12 };
            }
        } else {
            ws.getRow(5).height = 15.75;
        }
    };

    // Force a uniform Calibri 12 across every cell of every sheet, and strip all
    // colour — background fills and font colours — leaving plain black text on white
    // (borders are kept, they are structure, not colour). Applied to the report
    // export and all certificate exports just before the file is written.
    const applyCalibri12 = (workbook) => {
        // The Total / Grand Total rows keep their grey background; every other fill
        // (orange headers, etc.) and all font colours are stripped to plain black/white.
        const KEEP_FILL = 'FFD9D9D9';
        workbook.eachSheet((sheet) => {
            sheet.eachRow({ includeEmpty: true }, (row) => {
                row.eachCell({ includeEmpty: true }, (cell) => {
                    const { color, ...restFont } = cell.font || {};
                    cell.font = { ...restFont, name: 'Calibri', size: 12 };
                    const argb = cell.fill && cell.fill.fgColor && cell.fill.fgColor.argb;
                    if (argb !== KEEP_FILL) {
                        cell.fill = { type: 'pattern', pattern: 'none' };
                    }
                    if (typeof cell.value === 'string') {
                        cell.value = cell.value.trim();
                    }
                });
            });
        });
    };

    // Force a uniform Calibri 12 across every cell of the RDN workbook, matching the
    // reference template's font everywhere — but (unlike applyCalibri12 above) leave
    // every fill untouched, since the RDN sheets use several distinct fills (grey
    // FFF2F2F2 banners/headers/totals, and plain unfilled data/signature rows) that
    // must all survive as set by each sheet builder.
    const applyRdnCalibri12 = (workbook) => {
        workbook.eachSheet((sheet) => {
            sheet.eachRow({ includeEmpty: true }, (row) => {
                row.eachCell({ includeEmpty: true }, (cell) => {
                    const { color, ...restFont } = cell.font || {};
                    cell.font = { ...restFont, name: 'Calibri', size: 12 };
                });
            });
        });
    };

    const exportToExcel = async () => {
        let workbook = new ExcelJS.Workbook();
        const exportDate = dayjs().format('DD.MM.YYYY');
        // Captured once Import St's full data/footer extent is known, then used after the
        // workbook buffer is written to patch in the companion _xlnm._FilterDatabase defined
        // name ExcelJS never writes on its own (see addFilterDatabaseName).
        let importStFilterRange = null;

        const addSheet = (sheetName, data, columns) => {
            if (!data || data.length === 0) return;
            let worksheet = workbook.addWorksheet(sheetName);
            worksheet.columns = columns.map(col => ({
                header: col.header || col.title,
                key: col.dataIndex || col.key,
                width: col.excelWidth || col.width || 20
            }));

            let maxHeaderLines = 1;
            worksheet.getRow(1).eachCell((cell, colIdx) => {
                const col = columns[colIdx - 1];
                const charWidth = col ? (col.excelWidth || 20) : 20;
                const hdrLines = getLines(cell.value, charWidth);
                if (hdrLines > maxHeaderLines) maxHeaderLines = hdrLines;
                cell.font = { bold: true };
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FFC000' },
                };
                cell.alignment = { vertical: 'middle', wrapText: true };
            });
            worksheet.getRow(1).height = Math.max(20, maxHeaderLines * 16);

            data.forEach(obj => {
                let rowData = {};
                columns.forEach(col => {
                    rowData[col.dataIndex] = obj[col.dataIndex];
                });
                const addedRow = worksheet.addRow(rowData);
                let maxLines = 1;
                columns.forEach((col, idx) => {
                    const val = obj[col.dataIndex];
                    const charWidth = col.excelWidth || 20;
                    const lines = getLines(val, charWidth);
                    if (lines > maxLines) {
                        maxLines = lines;
                    }
                    const cell = addedRow.getCell(idx + 1);
                    cell.alignment = { vertical: 'middle', wrapText: true };
                });
                addedRow.height = Math.max(20, maxLines * 16);
            });
        };

        // ── Import Statement sheet ─────────────────────────────────────────────
        const addImportStatementSheet = () => {
            if (!importStData || importStData.length === 0) return;
            const ws = workbook.addWorksheet('Import St');

            ws.pageSetup = {
                paperSize: 9,
                orientation: 'portrait',
                margins: { left: 0.7, right: 0.7, top: 0.75, bottom: 0.75, header: 0, footer: 0 }
            };

            // ─── FIX: licData lookup with safe string coercion ────────────────
            const licData = masterSummaryData.find(
                item => String(item.lic_no || '').trim() === String(billNo || '').trim()
            ) || masterSummaryData[0] || {};

            // Column A = narrow empty spacer.
            // Columns B–M = CIF block (12 data cols).
            // Column N    = blank separator (matches reference template).
            // Columns O–Z = BOE / Duty / Balance block (12 data cols, total 25 cols including spacer).
            const allColWidths = [
                8, 32, 15, 8, 12, 18, 18,    // B(1)–H(7): CIF block first 7
                13.71, 13.71, 12, 15.71, 15.71, // I(8)–M(12): CIF block last 5 (BE No, BE Date, Qty, CIF INR, CIF USD)
                7.14,                         // N(13): blank separator
                22, 16, 10, 10, 10, 16, 16, 16, 20, 14, 18, 18 // O(14)–Z(25): BOE block
            ];
            ws.columns = [{ width: 3 }, ...allColWidths.map((w) => ({ width: w }))];

            const grey = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF2F2F2' } };
            const thin = { style: 'thin' };
            const allBorders = { top: thin, left: thin, bottom: thin, right: thin };

            // 24 data columns: B–M (CIF block) + O–Z (BOE/Duty/Balance block)
            // Col N is a blank separator — excluded from colLetters (we handle it manually)
            const colLetters = ['B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'];
            const lastColLetter = 'Z';

            // Header rows 1-5: reference merges only to column M, not the full table width
            writeSheetHeader(ws, {
                lastColLetter,
                headerLastColLetter: 'M',
                companyName: selectedIECName,
                licNo: billNo,
                licDate: licData.lic_date ? dayjs(licData.lic_date).format('DD.MM.YYYY') : '',
                fileNo: licData.file_no,
                titleText: 'IMPORT STATEMENT',
            });

            // ─── Row 6: Section banner, B6:M6 only (reference leaves O6:Z6 blank/unstyled) ─
            ws.getRow(6).height = 21;
            setMergedCell(ws, `B6:M6`,
                'DETAILS OF IMPORT MADE AGAINST THE AUTHORISATION',
                {
                    bold: true,
                    horizontal: 'center',
                    fill: grey,
                    fontSize: 12,
                }
            );
            ['B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M'].forEach(col => {
                const cell = ws.getCell(`${col}6`);
                cell.border = allBorders;
                cell.fill = grey;
                cell.font = { bold: true, size: 12 };
            });

            // Row 7: Table Column Headers
            // Column N (index 12 in colLetters would be O) is skipped — it is the blank separator.
            // Col 7 and 12 must name the licence's own currency (e.g. EUR/USD).
            const licCurrency = String(licData.cif_currency || '').trim().toUpperCase() || 'USD';
            ws.getRow(7).height = 60;
            // 24 header names mapped to colLetters (which skips N)
            const headers = [
                'Import Sr.No.', 'Item Imported', 'Qty Allowed In the Authorisation ',
                'UOM', 'ITCHS Code',
                'CIF Value Allowed In the Authorisation\n(IN INR)',
                `CIF Value Allowed In the Authorisation\n(IN ${licCurrency})`,
                'Bill of Entry No', 'Bill of Entry Date', 'Quantity Imported',
                `CIF Value \n(IN ${licCurrency})`, 'CIF Value \n(IN INR)',
                // — col N is blank separator (skipped) — BOE block starts at O:
                'BOE Reference Code',
                'Assessable Value', 'BCD % Duty Rate', 'SWS % Duty Rate', 'IGST % Duty Rate',
                'BCD Exempted', 'SWS Exempted', 'IGST Exempted', 'Total Duty Exempted',
                'Import Bal - Qty', 'Import Bal - CIF Value in INR', `Import Bal - CIF Value in ${licCurrency}`,
            ];
            headers.forEach((hdr, i) => {
                const cell = ws.getCell(`${colLetters[i]}7`);
                cell.value = hdr;
                cell.font = { bold: true, size: 12 };
                cell.fill = grey;
                cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
                cell.border = allBorders;
            });

            // Row 8: Column numbers (1–24, skipping the blank separator col N) — reference leaves this row unfilled
            ws.getRow(8).height = 13.5;
            headers.forEach((_, i) => {
                const cell = ws.getCell(`${colLetters[i]}8`);
                cell.value = i + 1;
                cell.font = { bold: true, size: 12 };
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
                cell.border = allBorders;
            });

            const fields = [
                'import_sr_no', 'item_imported', 'qty_allowed', 'uom', 'itchs_code',
                'cif_allowed_inr', 'cif_allowed_usd', 'be_no', 'be_date', 'qty_imported',
                'cif_imported_usd', 'cif_imported_inr',
                // index 12 in fields maps to colLetters[12] = O (blank N skipped)
                'reference_code', 'assessable_value',
                'bcd_rate', 'sws_rate', 'igst_rate', 'bcd_exempted', 'sws_exempted',
                'igst_exempted', 'total_duty_exempted', 'import_bal_qty', 'import_bal_cif_inr', 'import_bal_cif_usd'
            ];
            const sumCols = new Set([9, 10, 11, 13, 17, 18, 19, 20]);

            const processedImportData = (importStData || []).map(row => ({
                ...row,
                cif_allowed_usd: row.cif_allowed_usd != null ? Number(row.cif_allowed_usd) : null,
            }));

            const groups = [];
            const groupMap = new Map();
            processedImportData.forEach(row => {
                const key = row.import_sr_no != null ? String(row.import_sr_no) : '__null__';
                if (!groupMap.has(key)) {
                    groupMap.set(key, []);
                    groups.push({ key, rows: groupMap.get(key) });
                }
                groupMap.get(key).push(row);
            });

            const grandTotals = new Array(fields.length).fill(null);
            const numCols = colLetters.length;

            const accountingFmt = '_(* #,##0.00_);_(* \\(#,##0.00\\);_(* "-"??_);_(@_)';

            const styleTotal = (excelRow) => {
                excelRow.height = 19.5;
                // Reference gives every column (including blank B) the grey band/border on the
                // TOTAL row. Only the "Import Bal" block (X/Y/Z, indices 21-23) gets centered +
                // wrapped like the TOTAL label itself; every other numeric column is left at the
                // default (general/right) alignment, same as the GRAND TOTAL row.
                colLetters.forEach((colLetter, i) => {
                    const cell = excelRow.getCell(colLetter);
                    cell.font = { bold: true, size: 12 };
                    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF2F2F2' } };
                    cell.border = allBorders;
                    const isNumericImport = [2, 5, 6, 9, 10, 11, 13, 17, 18, 19, 20, 21, 22, 23].includes(i);
                    const isImportBalCol = [21, 22, 23].includes(i);
                    if (isNumericImport) {
                        cell.numFmt = accountingFmt;
                    }
                    if (isImportBalCol || i === 1) {
                        cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
                    } else if (isNumericImport) {
                        // No explicit horizontal (reference leaves these at the default/general
                        // alignment, which right-aligns numbers on its own) but still vertically
                        // centered.
                        cell.alignment = { vertical: 'middle' };
                    } else {
                        cell.alignment = { horizontal: 'center', vertical: 'middle' };
                    }
                });
            };

            groups.forEach(({ key, rows: groupRows }) => {
                const groupTotals = new Array(fields.length).fill(null);

                // ── Licence-level fields: take from first row only (same value on every BOE row)
                const licRow = groupRows[0];
                groupTotals[2] = licRow.qty_allowed != null ? Number(licRow.qty_allowed) : null;       // col 2 = qty_allowed
                groupTotals[5] = licRow.cif_allowed_inr != null ? Number(licRow.cif_allowed_inr) : null; // col 5 = cif_allowed_inr
                groupTotals[6] = licRow.cif_allowed_usd != null ? Number(licRow.cif_allowed_usd) : null; // col 6 = cif_allowed_usd

                // ── Transaction-level fields: sum across all BOE rows
                groupRows.forEach(row => {
                    fields.forEach((field, i) => {
                        if (sumCols.has(i)) {
                            groupTotals[i] = (groupTotals[i] || 0) + (Number(row[field]) || 0);
                        }
                    });
                });

                // ── Balance = allowed − total imported (or pull import_bal_cif_usd directly from database view)
                groupTotals[21] = (groupTotals[2] !== null) ? (groupTotals[2] - (groupTotals[9] || 0)) : null;
                groupTotals[22] = (groupTotals[5] !== null) ? (groupTotals[5] - (groupTotals[11] || 0)) : null;
                groupTotals[23] = licRow.import_bal_cif_usd != null ? Number(licRow.import_bal_cif_usd) : ((groupTotals[6] !== null) ? (groupTotals[6] - (groupTotals[10] || 0)) : null);

                const importLicenseLevelCols = new Set([0, 1, 2, 3, 4, 5, 6]);

                groupRows.forEach((row, rowIdx) => {
                    const dataRow = ws.addRow([]);
                    let maxLines = 1;
                    fields.forEach((field, i) => {
                        // Use colLetters[i] to correctly skip the blank col N separator
                        const cell = dataRow.getCell(colLetters[i]);

                        if ([21, 22, 23].includes(i)) {
                            cell.value = null;
                        } else if (rowIdx > 0 && importLicenseLevelCols.has(i)) {
                            cell.value = null;
                        } else if (field === 'be_date' && row[field]) {
                            // Parse only date part (YYYY-MM-DD) to avoid UTC→IST timezone shift showing 1 day early
                            const dateOnly = String(row[field]).split('T')[0];
                            const [yr, mo, dy] = dateOnly.split('-').map(Number);
                            cell.value = new Date(Date.UTC(yr, mo - 1, dy, 12, 0, 0));
                            cell.numFmt = '[$-409]d\\-mmm\\-yy';
                        } else if (field === 'be_no' && row[field] != null) {
                            cell.value = Number(row[field]);
                            cell.numFmt = '0';
                        } else if ((field === 'cif_allowed_inr' || field === 'cif_allowed_usd') && row[field] != null) {
                            cell.value = Number(row[field]);
                        } else if (['bcd_rate', 'sws_rate', 'igst_rate'].includes(field) && row[field] != null) {
                            cell.value = Number(row[field]) / 100;
                            cell.numFmt = '0.00%';
                        } else {
                            cell.value = row[field] ?? null;
                        }

                        cell.border = allBorders;
                        const colWidth = allColWidths[i >= 12 ? i + 1 : i] || 20; // +1 to skip separator at index 12
                        const lines = getLines(cell.value, colWidth);
                        if (lines > maxLines) maxLines = lines;

                        // Apply accounting format to numeric amount/quantity columns.
                        // Qty allowed is formatted separately below, including totals.
                        const isNumericImport = [5, 6, 9, 10, 11, 13, 17, 18, 19, 20, 21, 22, 23].includes(i);
                        const isPctCol = ['bcd_rate', 'sws_rate', 'igst_rate'].includes(field);
                        // Columns G/H (CIF Allowed In/USD, indices 5-6) keep their accounting
                        // number format but — unlike every other numeric column — the reference
                        // leaves them at the default (general) horizontal alignment, not 'right'.
                        const isDefaultAlignCol = [5, 6].includes(i);
                        // Reference doesn't wrap these columns' data-row cells even though it
                        // does wrap most others: BE No/Date, Qty Imported, CIF Imported USD/INR,
                        // and Assessable Value (indices 7-11, 13).
                        const noWrapCols = [5, 6, 7, 8, 9, 10, 11, 13].includes(i);
                        if (isNumericImport && !isPctCol) {
                            cell.numFmt = accountingFmt;
                            cell.alignment = {
                                horizontal: isDefaultAlignCol ? undefined : 'center',
                                vertical: 'middle',
                                wrapText: !noWrapCols
                            };
                        } else {
                            cell.alignment = {
                                horizontal: i === 1 ? 'left' : (isPctCol ? 'right' : 'center'),
                                vertical: 'middle',
                                wrapText: !noWrapCols
                            };
                        }
                    });
                    dataRow.height = Math.max(28, maxLines * 16);
                });

                const totalRow = ws.addRow([]);
                fields.forEach((field, i) => {
                    const cell = totalRow.getCell(colLetters[i]);
                    if (i === 1) {
                        cell.value = 'TOTAL';
                    } else if (groupTotals[i] !== null) {
                        cell.value = Math.round(groupTotals[i] * 100) / 100;
                        grandTotals[i] = (grandTotals[i] || 0) + groupTotals[i];
                    }
                });
                styleTotal(totalRow);
            });

            if (groups.length > 1) {
                const gtRow = ws.addRow([]);
                fields.forEach((field, i) => {
                    const cell = gtRow.getCell(colLetters[i]);
                    // "GRAND TOTAL" sits in column C (index 1), same column as each group's own
                    // "TOTAL" label — not column B, which the reference leaves blank here too.
                    if (i === 1) {
                        cell.value = 'GRAND TOTAL';
                    } else if (grandTotals[i] !== null) {
                        cell.value = Math.round(grandTotals[i] * 100) / 100;
                    }
                });
                gtRow.height = 21.75;
                // Apply styling over colLetters (skips blank col N separator) — unlike the per-item
                // TOTAL rows, the reference's GRAND TOTAL row styles column B too (it holds no
                // label but still gets the grey band/border).
                // Reference drops the bottom border specifically on columns C–M (indices 1-11) —
                // column B and the O–Z block (indices 12-23) keep the full box.
                const thinBorder = { style: 'thin' };
                colLetters.forEach((colLetter, i) => {
                    const cell = gtRow.getCell(colLetter);
                    cell.font = { bold: true, size: 12 };
                    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF2F2F2' } };
                    const dropBottom = i >= 1 && i <= 11;
                    cell.border = dropBottom
                        ? { top: thinBorder, left: thinBorder, right: thinBorder }
                        : allBorders;
                    const isNumericImport = [2, 5, 6, 9, 10, 11, 13, 17, 18, 19, 20, 21, 22, 23].includes(i);
                    if (isNumericImport) {
                        cell.numFmt = accountingFmt;
                    }
                    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: colLetter === 'B' };
                });
            }

            // Use literal grouping commas so the lakh/crore layout survives Excel locale changes.
            const quantityFormat = new Intl.NumberFormat('en-IN', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });
            for (let rowNumber = 9; rowNumber <= ws.lastRow.number; rowNumber++) {
                const cell = ws.getCell(`${colLetters[2]}${rowNumber}`);
                if (cell.value != null && cell.value !== '') {
                    cell.value = Number(cell.value);
                    cell.numFmt = quantityFormat.format(Math.abs(cell.value))
                        .replace(/\d/g, '0').replace(/,/g, '","');
                }
                cell.alignment = { ...cell.alignment, horizontal: 'right' };
            }

            // Bottom Signature Footer
            const row1Num = ws.lastRow.number + 1;
            const r1 = ws.addRow([]);
            r1.height = 19.5;

            ws.addRow([]).height = 20; // Row 2 of the box (empty)
            ws.addRow([]).height = 20; // Row 3 of the box (empty)

            const row4Num = ws.lastRow.number + 1;
            const r4 = ws.addRow([]);
            r4.height = 19.5;

            const impFormattedDate = exportDate;

            // Row 1 values — reference leaves these as single, unmerged cells (text overflows
            // visually across the empty cells to the right), same as the rows 1-3 banner.
            const cellLeft1 = ws.getCell(`B${row1Num}`);
            cellLeft1.value = `Date:- ${impFormattedDate}`;
            cellLeft1.font = { name: 'Calibri', bold: true, size: 12 };
            cellLeft1.alignment = { horizontal: 'left', vertical: 'middle' };

            const cellRight1 = ws.getCell(`I${row1Num}`);
            cellRight1.value = `FOR ${stripTradeSuffix(selectedIECName)}`;
            cellRight1.font = { name: 'Calibri', bold: true, size: 12 };

            // Row 4 values
            const cellLeft2 = ws.getCell(`B${row4Num}`);
            cellLeft2.value = 'Place :-';
            cellLeft2.font = { name: 'Calibri', bold: true, size: 12 };
            cellLeft2.alignment = { horizontal: 'left', vertical: 'middle' };

            const cellRight2 = ws.getCell(`I${row4Num}`);
            cellRight2.value = 'Authorised Signatory';
            cellRight2.font = { name: 'Calibri', bold: true, size: 12 };

            // Apply outer box border: from Row 1 to Row 4, columns B (2) to M (13) — matches reference,
            // which does NOT extend this box out to column Z.
            applyOuterBorder(ws, row1Num, row4Num, 2, 13, 'thin');

            // Filter dropdowns spanning the header row through one row past the signature
            // footer — matches the reference's own AutoFilter extent exactly.
            const filterLastRow = row4Num + 1;
            ws.autoFilter = `B7:Z${filterLastRow}`;
            importStFilterRange = `$B$7:$Z$${filterLastRow}`;
        };

        // ── Export Statement sheet ─────────────────────────────────────────────
        const addExportStatementSheet = () => {
            if (!exportStData || exportStData.length === 0) return;
            const ws = workbook.addWorksheet('Export St');

            ws.pageSetup = {
                paperSize: 9,
                orientation: 'portrait',
                margins: { left: 0.7, right: 0.7, top: 0.75, bottom: 0.75, header: 0, footer: 0 }
            };

            // FIX: safe licData lookup
            const licData = masterSummaryData.find(
                item => String(item.lic_no || '').trim() === String(billNo || '').trim()
            ) || masterSummaryData[0] || {};

            // Layout:
            //   Col A = narrow empty spacer
            //   Cols B–P = 15 data columns (Export SR No → TOTAL REALISED VALUE IN Lic-Currency)
            //   Col Q    = blank separator (matches reference template structure)
            //   Cols R–X = 7 BRC-working + item-value columns (REFERENCE CODE … ex-rate)
            // Total named data columns = 22; total physical data cols = 23 (B–X, with Q blank)
            const expColWidths = [
                // 9.01, not 9 — ExcelJS treats exactly 9 as its own internal default column
                // width and silently omits it from the file, which would make viewers fall back
                // to their own default (~8.43) instead of the reference's actual 9.0.
                9.01,  // B  Col 1  EXPORT SR NO
                33.14, // C  Col 2  PRODUCT EXPORTED
                12,    // D  Col 3  ITC HS CODE
                13.71, // E  Col 4  SHIPPING BILL NO
                13.71, // F  Col 5  SHIPPING BILL DATE
                12,    // G  Col 6  EXPORT QTY
                8,     // H  Col 7  UOM
                11.71, // I  Col 8  CURRENCY
                10.57, // J  Col 9  SHB EXCHG RATE
                15.57, // K  Col 10 Direct Export - FOB in INR
                16.86, // L  Col 11 Direct Export - FOB in FC
                16.86, // M  Col 12 Direct Export - FOB in Licence Currency
                25,  // N  Col 21 BRC Amount Excluding Insurance & Freight in FC
                19.71, // O  Col 13 TOTAL REALISED VALUE IN INR
                19.29, // P  Col 14 TOTAL REALISED VALUE IN Licence Currency
                6.86,  // Q         ← blank separator (no data)
                22,  // R  Col 15 REFERENCE CODE
                20,  // S  Col 16 Total FOB (INR)
                20,  // T  Col 17 TOTAL REALISED VALUE FC
                20,  // U  Col 18 FREIGHT VALUE FC
                15,  // V  Col 19 INSURANCE VAL FC
                20,  // W  Col 20 NET REALISED VALUE FC
                20,  // X  Col 22 As per license currency ex rate as per Shipping Bill date
                24.43, // Y Col 23 BRC Lot
                8.71,  // Z         ← trailing blank spacer (reference leaves it fully empty)
            ];

            // Column A = narrow empty spacer; 25 physical columns start at B (includes blank Q, Z)
            ws.columns = [{ width: 3 }, ...expColWidths.map(w => ({ width: w }))];

            const grey = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF2F2F2' } };
            const thin = { style: 'thin' };
            const allBorders = { top: thin, left: thin, bottom: thin, right: thin };

            // Keep logical field indices/numbering; column 21 maps to N, immediately after 12 in M.
            const expColLetters = [
                'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'O', 'P',
                // Q is blank separator
                'R', 'S', 'T', 'U', 'V', 'W', 'N', 'X'
            ];
            const lastColLetter = 'X'; // last physical column

            // Header rows 1-5: reference has NO title in row 5 for Export St (row 5 is a blank
            // spacer) — the "EXPORT STATEMENT" title instead lives in row 6 below, alongside the
            // "BRC working details" band. Reference also leaves rows 1-3 unmerged (plain single
            // cells), so collapse to the start column instead of spanning the table width.
            writeSheetHeader(ws, {
                lastColLetter,
                headerLastColLetter: 'B',
                companyName: selectedIECName,
                licNo: billNo,
                licDate: licData.lic_date ? dayjs(licData.lic_date).format('DD.MM.YYYY') : '',
                fileNo: licData.file_no,
            });

            // Row 6: Band section headers
            const secRow = ws.getRow(6);
            secRow.height = 19.5;

            // B6:P6 = "EXPORT STATEMENT" band
            ws.mergeCells('B6:P6');
            ws.getCell('B6').value = 'EXPORT STATEMENT';

            // Q6 = blank separator — leave empty (no border)
            ws.getCell('Q6').value = null;

            // S6:V6 = BRC working details (reference spans only columns S–V)
            ws.mergeCells('S6:V6');
            ws.getCell('S6').value = 'BRC working details';

            ['B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'O', 'P', 'R', 'S', 'T', 'U', 'V', 'W', 'N', 'X', 'Y'].forEach(col => {
                const cell = ws.getCell(`${col}6`);
                cell.font = { bold: true, size: 12 };
                cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
                cell.border = allBorders;
                cell.fill = grey;
            });

            // Row 7: Column Headers
            ws.getRow(7).height = 45;
            const expLicCurrency = String(licData.cif_currency || licData.eo_currency || '').trim().toUpperCase() || 'USD';
            const expHeaders = [
                'EXPORT SR NO',
                'PRODUCT EXPORTED',
                'ITC HS CODE',
                'SHIPPING BILL NO',
                'SHIPPING BILL DATE',
                'EXPORT QTY',
                'UOM',
                'CURRENCY',
                'SHB \nEXCHG RATE',
                'Direct Export - FOB in INR',
                'Direct Export - FOB in FC',
                `Direct Export - FOB in ${expLicCurrency}`,
                'TOTAL REALISED VALUE in INR',
                `TOTAL REALISED VALUE in ${expLicCurrency}`,
                // Q is blank separator — skipped in expColLetters
                'REFERENCE CODE',
                'Total FOB (INR)',
                'TOTAL REALISED VALUE FC',
                'FREIGHT VALUE FC',
                'INSURANCE VAL FC',
                'NET REALISED VALUE FC',
                'BRC Amount Excluding Insurance & Freight in FC',
                'As per license currency ex rate as per Shipping Bill date',
            ];
            expHeaders.forEach((hdr, i) => {
                const cell = ws.getCell(`${expColLetters[i]}7`);
                cell.value = hdr;
                cell.font = { bold: true, size: 12 };
                cell.fill = grey;
                cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
                cell.border = allBorders;
            });

            // Column Y ("BRC Lot", col 23) sits outside the indexed expFields/expColLetters data
            // mapping below — it shows the calculation basis ('FOB_VALUE'/'INV_VALUE')
            // used for that row's realised-value figures, from
            // adv_auth_export_report.brc_lot.
            const yHeaderCell = ws.getCell('Y7');
            yHeaderCell.value = 'BRC Lot';
            yHeaderCell.font = { bold: true, size: 12 };
            yHeaderCell.fill = grey;
            yHeaderCell.alignment = { horizontal: 'center', wrapText: true };
            yHeaderCell.border = allBorders;

            // Row 8: Column Numbers 1 to 23
            ws.getRow(8).height = 16;
            expColLetters.forEach((col, i) => {
                const cell = ws.getCell(`${col}8`);
                cell.value = i + 1;
                cell.font = { bold: true, size: 12 };
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
                cell.border = allBorders;
            });
            const yColNumCell = ws.getCell('Y8');
            yColNumCell.value = expColLetters.length + 1;
            yColNumCell.font = { bold: true, size: 11 };
            yColNumCell.alignment = { horizontal: 'center', vertical: 'bottom' };
            yColNumCell.border = allBorders;

            // Fields mapping — 22 data fields corresponding to expColLetters
            // (col Q = blank separator is skipped; expColLetters already excludes Q)
            const expFields = [
                'export_sr_no',              // B (Col 1)
                'product_exported',          // C (Col 2)
                'itc_hs_code',               // D (Col 3)
                'shipping_bill_no',          // E (Col 4)
                'shipping_bill_date',        // F (Col 5)
                'export_qty',                // G (Col 6)
                'uom',                       // H (Col 7)
                'currency',                  // I (Col 8)
                'shb_exchange_rate',         // J (Col 9)
                'fob_inr',                   // K (Col 10)
                'fob_fc',                    // L (Col 11)
                '__fob_usd_calc__',          // M (Col 12)
                'total_realised_value_inr',  // O (Col 13)
                'total_realised_value_usd',  // P (Col 14)
                // — col Q is blank separator (skipped) —
                '__ref_code__',              // R (Col 15)
                'shb_total_fob_inr',         // S (Col 16)
                'shb_realised_value_fc',     // T (Col 17)
                'shb_freight_fc',            // U (Col 18)
                'shb_insurance_fc',          // V (Col 19)
                'shb_net_realised_fc',       // W (Col 20)
                'net_realised_value_fc',     // N (Col 21)
                'usd_rate',                  // X (Col 22)
            ];

            // Columns to sum (indices in expFields, 0-based)
            const expSumCols = new Set([5, 9, 10, 11, 12, 13, 15, 16, 17, 18, 19, 20]);
            const sbLevelCols = new Set([15, 16, 17, 18, 19]); // shb_total_fob_inr, shb_realised_value_fc, shb_freight_fc, shb_insurance_fc, shb_net_realised_fc

            const expGroups = [];
            const expGroupMap = new Map();
            (exportStData || []).forEach(row => {
                const key = row.export_sr_no != null ? String(row.export_sr_no) : '__null__';
                if (!expGroupMap.has(key)) {
                    expGroupMap.set(key, []);
                    expGroups.push({ key, rows: expGroupMap.get(key) });
                }
                expGroupMap.get(key).push(row);
            });

            const expGrandSums = new Array(expFields.length).fill(null);
            const numExpCols = expColLetters.length;

            const styleExpTotal = (excelRow) => {
                excelRow.height = 30;
                // Use expColLetters to skip the blank col Q separator
                expColLetters.forEach((col, i) => {
                    const cell = excelRow.getCell(col);
                    cell.font = { bold: true, size: 12 };
                    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF2F2F2' } };
                    cell.border = allBorders;

                    const isNumeric = [5, 8, 9, 10, 11, 12, 13, 15, 16, 17, 18, 19, 20, 21].includes(i);
                    if (isNumeric) {
                        cell.alignment = { horizontal: 'right', vertical: 'middle' };
                        cell.numFmt = '#,##0.00';
                    } else {
                        // Reference centers column C ("PRODUCT EXPORTED") on the Total row,
                        // unlike data rows which left-align it.
                        cell.alignment = { horizontal: 'center', vertical: 'middle' };
                    }
                });
            };

            expGroups.forEach(({ rows: groupRows }) => {
                const groupTotals = new Array(expFields.length).fill(null);
                const seenSbInGroup = new Set();

                groupRows.forEach(row => {
                    const fobUsdCalc = (Number(row.fob_inr) && Number(row.usd_rate)) ? Number(row.fob_inr) / Number(row.usd_rate) : 0;
                    const sbKey = String(row.shipping_bill_no || '');
                    const isFirstInGroup = !seenSbInGroup.has(sbKey);
                    seenSbInGroup.add(sbKey);

                    expFields.forEach((field, i) => {
                        if (expSumCols.has(i)) {
                            let val = 0;
                            if (field === '__fob_usd_calc__') {
                                val = fobUsdCalc;
                            } else if (field === 'fob_fc' && String(row.currency).trim().toUpperCase() === 'INR') {
                                val = 0;
                            } else if (sbLevelCols.has(i)) {
                                // Only add SB-level header once per shipping bill in this group
                                val = isFirstInGroup ? (Number(row[field]) || 0) : 0;
                            } else {
                                val = Number(row[field]) || 0;
                            }
                            groupTotals[i] = (groupTotals[i] || 0) + val;
                        }
                    });
                });

                expFields.forEach((field, i) => {
                    if (expSumCols.has(i) && groupTotals[i] !== null) {
                        expGrandSums[i] = (expGrandSums[i] || 0) + groupTotals[i];
                    }
                });

                const seenSbInData = new Set();
                groupRows.forEach((row) => {
                    const dataRow = ws.addRow([]);
                    let maxLines = 1;
                    const sbKey = String(row.shipping_bill_no || '');
                    const isSubsequentSbRow = seenSbInData.has(sbKey);
                    seenSbInData.add(sbKey);

                    expFields.forEach((field, i) => {
                        // Use expColLetters[i] to correctly skip the blank col Q separator
                        const cell = dataRow.getCell(expColLetters[i]);
                        if (field === '__fob_usd_calc__') {
                            const fobInr = Number(row.fob_inr), usdRate = Number(row.usd_rate);
                            cell.value = (fobInr && usdRate) ? Math.round((fobInr / usdRate) * 100) / 100 : null;
                        } else if (field === '__ref_code__') {
                            cell.value = row.reference_code || row.ref_code || (row.shipping_bill_no ? `${row.shipping_bill_no}-${row.export_sr_no || 1}-1` : '');
                        } else if (field === 'shipping_bill_date' && row[field]) {
                            // Parse only date part (YYYY-MM-DD) to avoid UTC→IST timezone shift showing 1 day early
                            const dateOnly = String(row[field]).split('T')[0];
                            const [yr, mo, dy] = dateOnly.split('-').map(Number);
                            cell.value = new Date(Date.UTC(yr, mo - 1, dy, 12, 0, 0));
                            cell.numFmt = 'd-mmm-yy';
                        } else if (field.startsWith('__')) {
                            cell.value = null;
                        } else if (isSubsequentSbRow && sbLevelCols.has(i)) {
                            // Leave cell blank on subsequent lines of the same Shipping Bill to avoid visual duplicate and double-sum
                            cell.value = null;
                        } else {
                            const rawVal = row[field];
                            cell.value = (rawVal != null && !isNaN(Number(rawVal)) && typeof rawVal !== 'string') ? Number(rawVal) : (rawVal ?? null);
                        }
                        cell.border = allBorders;
                        const colWidth = ws.getColumn(expColLetters[i]).width || 20;
                        const lines = getLines(cell.value, colWidth);
                        if (lines > maxLines) maxLines = lines;

                        const isNumeric = [5, 8, 9, 10, 11, 12, 13, 15, 16, 17, 18, 19, 20, 21].includes(i);
                        if (isNumeric) {
                            cell.alignment = { horizontal: 'right', vertical: 'middle' };
                            cell.numFmt = '#,##0.00';
                        } else {
                            // Reference left-aligns column C ("PRODUCT EXPORTED") on data rows, and
                            // doesn't wrap Shipping Bill No/Date (indices 3-4) even though it wraps
                            // every other text column here.
                            const noWrap = [3, 4].includes(i);
                            cell.alignment = { horizontal: expColLetters[i] === 'C' ? 'left' : 'center', vertical: 'middle', wrapText: !noWrap };
                        }
                    });
                    // Column Y ("BRC Lot") — see the note by its header above for what this shows.
                    const yDataCell = dataRow.getCell('Y');
                    yDataCell.value = row.brc_lot ?? null;
                    yDataCell.border = allBorders;
                    yDataCell.alignment = { horizontal: 'center', vertical: 'middle' };
                    dataRow.height = Math.max(28, maxLines * 16);
                });

                const totalRow = ws.addRow([]);
                expFields.forEach((field, i) => {
                    const cell = totalRow.getCell(expColLetters[i]);
                    if (i === 1) {
                        cell.value = 'Total';
                    } else if (groupTotals[i] !== null) {
                        cell.value = Math.round(groupTotals[i] * 100) / 100;
                    }
                });
                styleExpTotal(totalRow);
                // Column Y border only on the TOTAL row too — no fill/bold, matching the reference.
                totalRow.getCell('Y').border = allBorders;
            });

            if (expGroups.length > 1) {
                const gtRow = ws.addRow([]);
                expFields.forEach((field, i) => {
                    const cell = gtRow.getCell(expColLetters[i]);
                    if (i === 1) cell.value = 'Grand Total';
                    else if (expSumCols.has(i) && expGrandSums[i] !== null) cell.value = Math.round(expGrandSums[i] * 100) / 100;
                });
                gtRow.height = 19.5;
                // Apply styling using expColLetters to skip blank col Q separator
                expColLetters.forEach((col, i) => {
                    const cell = gtRow.getCell(col);
                    cell.font = { bold: true, size: 12 };
                    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF2F2F2' } };
                    cell.border = allBorders;

                    const isNumeric = [5, 8, 9, 10, 11, 12, 13, 15, 16, 17, 18, 19, 20, 21].includes(i);
                    if (isNumeric) {
                        cell.alignment = { horizontal: 'right', vertical: 'middle' };
                        cell.numFmt = '#,##0.00';
                    } else {
                        cell.alignment = { horizontal: 'center', vertical: 'middle' };
                    }
                });
            }

            // Bottom Signature Footer
            const row1Num = ws.lastRow.number + 1;
            const r1 = ws.addRow([]);
            r1.height = 19.5;

            ws.addRow([]).height = 20; // Row 2 of the box (empty)
            ws.addRow([]).height = 20; // Row 3 of the box (empty)

            const row4Num = ws.lastRow.number + 1;
            const r4 = ws.addRow([]);
            r4.height = 19.5;

            const expFormattedDate = dayjs().format('DD.MM.YYYY');

            // Row 1 values — reference uses plain (unmerged) B and K cells, not merged ranges
            const cellLeft1 = ws.getCell(`B${row1Num}`);
            cellLeft1.value = `Date:- ${expFormattedDate}`;
            cellLeft1.font = { name: 'Calibri', bold: true, size: 12 };
            cellLeft1.alignment = { horizontal: 'left', vertical: 'middle' };

            const cellRight1 = ws.getCell(`K${row1Num}`);
            cellRight1.value = `FOR ${stripTradeSuffix(selectedIECName)}`;
            cellRight1.font = { name: 'Calibri', bold: true, size: 12 };

            // Row 4 values
            const cellLeft2 = ws.getCell(`B${row4Num}`);
            cellLeft2.value = 'Place :-';
            cellLeft2.font = { name: 'Calibri', bold: true, size: 12 };
            cellLeft2.alignment = { horizontal: 'left', vertical: 'middle' };

            const cellRight2 = ws.getCell(`K${row4Num}`);
            cellRight2.value = 'Authorised Signatory';
            cellRight2.font = { name: 'Calibri', bold: true, size: 12 };

            // Extend the footer through P to match the shifted Export Statement columns.
            applyOuterBorder(ws, row1Num, row4Num, 2, 16, 'thin');
        };

        // ── Import Ledger sheet ────────────────────────────────────────────────
        const addImportLedgerSheet = () => {
            if (!importLedgerData || importLedgerData.length === 0) return;
            const ws = workbook.addWorksheet('Import Ledger');

            ws.pageSetup = {
                paperSize: 9,
                orientation: 'portrait',
                margins: { left: 0.7, right: 0.7, top: 0.75, bottom: 0.75, header: 0, footer: 0 }
            };

            const colWidths = [15, 22, 38, 24, 22, 20, 26, 24, 18, 22, 8.71];
            ws.columns = colWidths.map(w => ({ width: w }));

            const thin = { style: 'thin' };
            const allBorders = { top: thin, left: thin, bottom: thin, right: thin };
            const colLetters = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J'];

            // Row 1: Title — no fill/border in the reference, just a bold left-aligned label
            ws.getRow(1).height = 24.75;
            setMergedCell(ws, 'A1:J1', 'Import Ledger', { bold: true, fontSize: 12, horizontal: 'left' });

            // Row 2: Column Headers — reference uses no fill here either (unlike the other sheets)
            ws.getRow(2).height = 45;
            const headersRow2 = [
                'Import Sr No',
                'Import Qty As per AA',
                'Import Description',
                'CIF Value (As per AA)',
                'CIF FCC (As per AA)',
                'Actual Import Qty',
                'Actual CIF Value \n(As per BOE)',
                'Actual CIF FCC \n(As per BOE)',
                'Import Bal Qty',
                'Bal Qty CIF in Rs'
            ];
            headersRow2.forEach((hdr, i) => {
                const cell = ws.getCell(`${colLetters[i]}2`);
                cell.value = hdr;
                cell.font = { bold: true, size: 12 };
                cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
                cell.border = allBorders;
            });

            // Row 3 onwards: Data rows
            importLedgerData.forEach((row, idx) => {
                const rowNum = idx + 3;
                const excelRow = ws.getRow(rowNum);
                excelRow.height = 39.75;

                const qtyAllowed = row.qty_allowed != null ? Number(row.qty_allowed) : 0;
                const cifInr = row.cif_allowed_inr != null ? Number(row.cif_allowed_inr) : 0;
                const qtyImported = row.qty_imported != null ? Number(row.qty_imported) : 0;
                const cifImportedInr = row.cif_imported_inr != null ? Number(row.cif_imported_inr) : 0;

                const balQty = qtyAllowed - qtyImported;
                const balCifInr = cifInr - cifImportedInr;

                excelRow.getCell(1).value = Number(row.import_sr_no) || (idx + 1);
                excelRow.getCell(2).value = qtyAllowed;
                excelRow.getCell(3).value = row.item_imported || '';
                excelRow.getCell(4).value = cifInr;
                excelRow.getCell(5).value = row.cif_allowed_usd != null ? Number(row.cif_allowed_usd) : null;
                excelRow.getCell(6).value = qtyImported !== 0 ? qtyImported : null;
                excelRow.getCell(7).value = cifImportedInr !== 0 ? cifImportedInr : null;
                const actualCifFcc = (row.debit_value_fcc && row.debit_value_fcc !== 0)
                    ? Number(row.debit_value_fcc)
                    : ((row.cif_imported_usd && row.cif_imported_usd !== 0) ? Number(row.cif_imported_usd) : null);
                excelRow.getCell(8).value = actualCifFcc;
                excelRow.getCell(9).value = balQty;
                excelRow.getCell(10).value = balCifInr;

                colLetters.forEach((col, i) => {
                    const cell = excelRow.getCell(i + 1);
                    cell.border = allBorders;
                    if (i === 2) {
                        cell.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
                    } else if ([0, 1].includes(i)) {
                        // Reference centers column B (Import Qty As per AA) like column A, not right.
                        cell.alignment = { horizontal: 'center', vertical: 'middle' };
                        if (i === 1) cell.numFmt = '#,##0.00;(#,##0.00);"-"';
                    } else {
                        cell.alignment = { horizontal: 'right', vertical: 'middle' };
                        if ([3, 4, 5, 6, 7, 8, 9].includes(i)) {
                            cell.numFmt = '#,##0.00;(#,##0.00);"-"';
                        }
                    }
                });
            });
        };

        addImportStatementSheet();
        addExportStatementSheet();

        // ── SHB Wise Accountability sheet ──────────────────────────────────────
        const addShbWiseAccountabilitySheet = () => {
            const filteredShb = (shbWiseData || []).filter(r =>
                !billNo || String(r.auth_number || '').trim() === String(billNo || '').trim()
            );
            if (!filteredShb || filteredShb.length === 0) return;
            const ws = workbook.addWorksheet('SHB Wise Accountability');

            ws.pageSetup = {
                paperSize: 9,
                orientation: 'portrait',
                margins: { left: 0.7, right: 0.7, top: 0.75, bottom: 0.75, header: 0, footer: 0 }
            };

            // ─── FIX: safe licData lookup ─────────────────────────────────────
            const licData = masterSummaryData.find(
                item => String(item.lic_no || '').trim() === String(billNo || '').trim()
            ) || masterSummaryData[0] || {};

            const grey = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF2F2F2' } };
            const thin = { style: 'thin' };
            const allBorders = { top: thin, left: thin, bottom: thin, right: thin };

            // Reference has no IEC NO / AUTH NUMBER columns at all on this sheet, and places
            // EXP IMP ITEM SR NO last (column S) instead of first — 19 physical columns A-S,
            // with R a blank separator (index 17, null placeholder below).
            const shbColWidths = [
                14,    // IMPORT ITEM SR NO (A)
                16,    // IMP QTY AS PER LIC (B)
                16,    // IMP UOM (C)
                36,    // IMPORT ITEM DESCRIPTION (D)
                12,    // EXP SR NO (E)
                36,    // EXP DESCRIPTION (F)
                16,    // EXP QTY AS PER AUTH (G)
                10.86, // EXP UOM (H)
                14,    // PER UNIT REQD (I)
                18,    // ACTUAL IMPORTS MADE (J)
                16,    // ACTUAL IMPORT UOM (K)
                16,    // SHB NO (L)
                14,    // SHB DT (M)
                16,    // ACTUAL EXPORT QTY (N)
                18,    // REF CODE (O)
                16,    // QTY USED (P)
                18,    // EXCESS IMPORT QTY (Q)
                8.71,  // blank separator (R)
                15.14, // EXP IMP ITEM SR NO (S)
                8.71,  // trailing blank spacer (T) — reference leaves it fully empty
            ];
            ws.columns = shbColWidths.map(w => ({ width: w }));

            const numCols = shbColWidths.length;
            const colLetters = Array.from({ length: numCols }, (_, i) => {
                if (i < 26) {
                    return String.fromCharCode(65 + i);
                } else {
                    return 'A' + String.fromCharCode(65 + (i - 26));
                }
            });
            const lastColLetter = colLetters[colLetters.length - 1];

            // ─── Shared writeSheetHeader starting at Column A ──────
            // The reference has NO merged cells anywhere on this sheet — the company/lic/file
            // banner text sits in single, unmerged A1/A2/A3 cells (it just overflows visually
            // across the empty cells to its right). bannerLastColLetter defaults to sCol, so
            // no merge is created.
            writeSheetHeader(ws, {
                startColLetter: 'A',
                lastColLetter: 'A',
                companyName: selectedIECName,
                licNo: billNo,
                licDate: licData.lic_date ? dayjs(licData.lic_date).format('DD.MM.YYYY') : '',
                fileNo: licData.file_no,
            });

            // Row 4: section title, immediately below FILE NO — also a single unmerged cell, no fill/box.
            setMergedCell(ws, 'A4:A4',
                'SHIPPING BILL WISE ACCOUNTABILITY STATEMENT',
                { bold: true, horizontal: 'left', fontSize: 12 });
            ws.getRow(6).height = 15.75; // blank spacer row between title and header

            // Row 7: Column headers. Index 17 (col R) is a blank separator — reference skips it
            // entirely, same as the other RDN sheets' spacer columns.
            ws.getRow(7).height = 60;
            const shbHeaders = [
                'IMPORT ITEM SR NO',
                'IMP QTY AS PER LIC',
                'IMP UOM',
                'IMPORT ITEM DESCRIPTION',
                'EXP SR NO',
                'EXP DESCRIPTION',
                'EXP QTY AS PER AUTH',
                'EXP UOM',
                'PER UNIT REQD',
                'ACTUAL IMPORTS MADE',
                'ACTUAL IMPORT UOM',
                'SHB NO',
                'SHB DT',
                'ACTUAL EXPORT QTY',
                'REF CODE',
                'QTY USED',
                'EXCESS IMPORT QTY',
                null,
                'EXP IMP ITEM SR NO',
            ];
            shbHeaders.forEach((hdr, i) => {
                if (hdr === null) return;
                const cell = ws.getCell(`${colLetters[i]}7`);
                cell.value = hdr;
                cell.font = { bold: true, size: 12 };
                cell.fill = grey;
                cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
                cell.border = allBorders;
            });

            // Row 8: Column numbers — reference leaves this row unfilled, like Import/Export St.
            // Numbering starts at 4 (not 1) and skips the blank separator column, a quirk carried
            // over verbatim from the reference template.
            ws.getRow(8).height = 13.5;
            const shbColNumbers = [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, null, 21];
            shbColNumbers.forEach((num, i) => {
                if (num === null) return;
                const cell = ws.getCell(`${colLetters[i]}8`);
                cell.value = num;
                cell.font = { bold: true, size: 12 };
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
                cell.border = allBorders;
            });

            // Matches shbHeaders/shbColNumbers' column order exactly: no IEC NO/AUTH NUMBER,
            // EXP IMP ITEM SR NO moved to the end, index 17 (col R) is the blank separator.
            const shbFields = [
                'import_item_sr_no',
                'imp_qty_as_per_lic',
                'imp_uom',
                'import_item_description',
                'exp_sr_no',
                'exp_description',
                'exp_qty_as_per_auth',
                'exp_uom',
                'per_unit_reqd',
                'actual_imports_made',
                'actual_import_uom',
                'shb_no',
                'shb_dt',
                'actual_export_qty',
                'ref_code',
                'qty_used',
                'excess_import_qty',
                null,
                'exp_imp_item_sr_no',
            ];

            // Group row data by exp_imp_item_sr_no
            const groups = [];
            const groupMap = new Map();
            filteredShb.forEach(row => {
                const key = row.exp_imp_item_sr_no != null ? String(row.exp_imp_item_sr_no) : '__null__';
                if (!groupMap.has(key)) {
                    groupMap.set(key, []);
                    groups.push({ key, rows: groupMap.get(key) });
                }
                groupMap.get(key).push(row);
            });

            groups.forEach(({ rows: groupRows }) => {
                const groupStartRow = ws.lastRow.number + 1;

                groupRows.forEach((row, idx) => {
                    const dataRow = ws.addRow([]);
                    let maxLines = 1;

                    shbFields.forEach((field, i) => {
                        if (field === null) return; // blank separator column (R)
                        const cell = dataRow.getCell(i + 1);

                        // If not the first row in this group, suppress the duplicate actual imports columns
                        if (idx > 0 && ['actual_imports_made', 'actual_import_uom'].includes(field)) {
                            cell.value = null;
                        } else if (field === 'excess_import_qty') {
                            // Leave individual row excess import qty empty (summarized at the total row)
                            cell.value = null;
                        } else if (field === 'shb_dt' && row[field]) {
                            // Reference stores this as a plain DD-MM-YYYY text string, not a real date.
                            cell.value = dayjs(row[field]).format('DD-MM-YYYY');
                        } else if (field === 'exp_imp_item_sr_no') {
                            // Reference displays a composed "Exp.{n}-Imp.{n}" label here, not the raw id.
                            cell.value = `Exp.${row.exp_sr_no ?? ''}-Imp.${row.import_item_sr_no ?? ''}`;
                        } else {
                            const rawVal = row[field];
                            cell.value = (rawVal != null && !isNaN(Number(rawVal)) && typeof rawVal !== 'string')
                                ? Number(rawVal)
                                : (typeof rawVal === 'string' ? rawVal.trim() : (rawVal ?? null));
                        }

                        cell.border = allBorders;

                        if (typeof cell.value === 'number') {
                            if (field === 'per_unit_reqd') {
                                cell.numFmt = '0.0000';
                            } else if (['imp_qty_as_per_lic', 'exp_qty_as_per_auth', 'actual_imports_made', 'actual_export_qty', 'qty_used', 'excess_import_qty'].includes(field)) {
                                cell.numFmt = '#,##0.00;(#,##0.00);"-"';
                            }
                        }

                        const colWidth = shbColWidths[i] || 14;
                        const lines = getLines(cell.value, colWidth);
                        if (lines > maxLines) maxLines = lines;
                        cell.alignment = { horizontal: [3, 5].includes(i) ? 'left' : 'center', vertical: 'middle', wrapText: true };
                    });

                    dataRow.height = Math.max(20, maxLines * 16);
                });

                const groupEndRow = ws.lastRow.number;

                // Add group total row
                const totalRow = ws.addRow([]);
                totalRow.height = 20;

                // Pre-calculate numerical totals in JS to eliminate Excel string formulas.
                // actual_imports_made is a group-level (per import item) value repeated on
                // every row — take it ONCE (max), not summed, or the total inflates by the
                // number of rows in the group. actual_export_qty / qty_used are per-row.
                let actualImportsMade = 0;
                let sumExportQty = 0;
                let sumQtyUsed = 0;
                groupRows.forEach(r => {
                    actualImportsMade = Math.max(actualImportsMade, Number(r.actual_imports_made) || 0);
                    sumExportQty += Number(r.actual_export_qty) || 0;
                    sumQtyUsed += Number(r.qty_used) || 0;
                });
                const excessImportQty = actualImportsMade - sumQtyUsed;

                shbFields.forEach((field, i) => {
                    if (field === null) return; // blank separator column (R) — stays fully unstyled
                    const cell = totalRow.getCell(i + 1);
                    cell.font = { bold: true, size: 12 };
                    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF2F2F2' } };
                    cell.border = allBorders;
                    cell.alignment = { horizontal: i === 5 ? 'left' : 'center', vertical: 'middle' };

                    if (i === 5) {
                        cell.value = 'Total';
                    } else if (field === 'actual_imports_made') {
                        cell.value = Math.round(actualImportsMade * 100) / 100;
                        cell.numFmt = '#,##0.00;(#,##0.00);"-"';
                    } else if (field === 'actual_import_uom') {
                        // UOM belongs to detail rows only; keep the Total row blank.
                        cell.value = null;
                    } else if (field === 'actual_export_qty') {
                        cell.value = Math.round(sumExportQty * 100) / 100;
                        cell.numFmt = '#,##0.00;(#,##0.00);"-"';
                    } else if (field === 'qty_used') {
                        cell.value = Math.round(sumQtyUsed * 100) / 100;
                        cell.numFmt = '#,##0.00;(#,##0.00);"-"';
                    } else if (field === 'excess_import_qty') {
                        cell.value = Math.round(excessImportQty * 100) / 100;
                        cell.numFmt = '#,##0.00;(#,##0.00);"-"';
                    }
                });
            });
        };
        addShbWiseAccountabilitySheet();

        const addMasterSummarySheet = () => {
            if (!masterSummaryData || masterSummaryData.length === 0) return;
            const ws = workbook.addWorksheet('Adv Auth Master Summary');

            ws.pageSetup = {
                paperSize: 9,
                orientation: 'landscape',
                fitToPage: true,
                fitToWidth: 1,
                fitToHeight: 0,
                margins: { left: 0.25, right: 0.25, top: 0.5, bottom: 0.5, header: 0.2, footer: 0.2 }
            };

            // Find the active license row
            const licData = masterSummaryData.find(
                item => String(item.lic_no || '').trim() === String(billNo || '').trim()
            ) || masterSummaryData[0] || {};

            // Define Excel Columns (exactly matching the screenshot)
            const excelCols = [
                { header: 'LIC NO', dataIndex: 'lic_no', width: 18 },
                { header: 'LIC DATE', dataIndex: 'lic_date', width: 15, isDate: true },
                { header: 'FILE NO', dataIndex: 'file_no', width: 22 },
                { header: 'CIF ALLOWED INR', dataIndex: 'cif_allowed_inr', width: 22, isNumber: true },
                { header: 'CIF ALLOWED USD', dataIndex: 'cif_allowed_usd', width: 22, isNumber: true },
                { header: 'EXPORT OBLIGATION INR', dataIndex: 'export_obligation_inr', width: 26, isNumber: true },
                { header: 'EXPORT OBLIGATION USD', dataIndex: 'export_obligation_usd', width: 26, isNumber: true },
                { header: 'EOP DATE', dataIndex: 'eop_date', width: 15, isDate: true },
                { header: 'Type of Norm', dataIndex: 'type_of_norm', width: 18 },
                { header: 'CIF Currency', dataIndex: 'cif_currency', width: 14 },
                { header: 'EO Currency', dataIndex: 'eo_currency', width: 14 },
                { header: 'Add', dataIndex: 'address', width: 25 },
                { header: 'Name of Company', dataIndex: 'company_name', width: 35 },
                { header: '', dataIndex: 'empty_n', width: 5 }, // Tiny separator column
                { header: 'REGIONAL OFFICE', dataIndex: 'dgft_regional_office', width: 22 },
                { header: 'IEC NO', dataIndex: 'iec_no', width: 15 },
                { header: 'TOTAL ACTUAL EXPORT INR', dataIndex: 'total_actual_export_inr', width: 26, isNumber: true },
                { header: 'TOTAL ACTUAL EXPORT USD', dataIndex: 'total_actual_export_usd', width: 26, isNumber: true },
                { header: 'TOTAL ACTUAL IMPORT INR', dataIndex: 'total_actual_import_inr', width: 26, isNumber: true },
                { header: 'TOTAL ACTUAL IMPORT USD', dataIndex: 'total_actual_import_usd', width: 26, isNumber: true },
                { header: 'VALUE ADDITION PERCENT', dataIndex: 'value_addition_percent', width: 26, isPercent: true }
            ];

            ws.columns = excelCols.map(col => ({ width: col.width }));

            const orange = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFC000' } };
            const thin = { style: 'thin' };
            const allBorders = { top: thin, left: thin, bottom: thin, right: thin };

            const numCols = excelCols.length;
            const colLetters = Array.from({ length: numCols }, (_, i) => {
                if (i < 26) {
                    return String.fromCharCode(65 + i);
                } else {
                    return 'A' + String.fromCharCode(65 + (i - 26));
                }
            });

            // Write Row 1: Headers
            ws.getRow(1).height = 40;
            excelCols.forEach((col, i) => {
                const cell = ws.getCell(`${colLetters[i]}1`);
                cell.value = col.header;
                cell.font = { bold: true, size: 10, name: 'Calibri' };
                if (col.header) {
                    cell.fill = orange;
                }
                cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
                cell.border = allBorders;
            });

            // Write Row 2... onwards: Data rows
            masterSummaryData.forEach((row, rowIndex) => {
                const addedRow = ws.addRow([]);
                let maxLines = 1;
                excelCols.forEach((col, idx) => {
                    const cell = addedRow.getCell(idx + 1);
                    const field = col.dataIndex;

                    if (field === 'empty_n') {
                        cell.value = '';
                    } else if (col.isDate && row[field]) {
                        cell.value = dayjs(row[field]).format('DD-MMM-YY');
                    } else {
                        const rawVal = row[field];
                        let val = (rawVal != null && !isNaN(Number(rawVal)) && typeof rawVal !== 'string')
                            ? Number(rawVal)
                            : (typeof rawVal === 'string' ? rawVal.trim() : (rawVal ?? null));
                        if (col.isPercent && typeof val === 'number') {
                            val = val / 100;
                        }
                        cell.value = val;
                    }
                    cell.border = allBorders;

                    if (typeof cell.value === 'number') {
                        if (col.isPercent) {
                            cell.numFmt = '0.00%';
                        } else {
                            cell.numFmt = '#,##0.00;(#,##0.00);"-"';
                        }
                        cell.alignment = { vertical: 'middle', wrapText: true, horizontal: 'right' };
                    } else {
                        cell.alignment = { vertical: 'middle', wrapText: true, horizontal: 'center' };
                    }

                    const charWidth = col.width || 20;
                    const lines = getLines(cell.value, charWidth);
                    if (lines > maxLines) maxLines = lines;
                });
                addedRow.height = Math.max(20, maxLines * 16);
            });
        };
        // NOTE: "Adv Auth Master Summary" sheet disabled in the Excel export (per request).
        // Re-enable by uncommenting the call below (addMasterSummarySheet is still defined above).
        // addMasterSummarySheet();

        const addDatabaseSheet = () => {
            if (!masterSummaryData || masterSummaryData.length === 0) return;
            const ws = workbook.addWorksheet('Data base');

            ws.pageSetup = {
                paperSize: 9,
                orientation: 'portrait',
                margins: { left: 0.7, right: 0.7, top: 0.75, bottom: 0.75, header: 0, footer: 0 }
            };

            const thin = { style: 'thin' };
            const allBorders = { top: thin, left: thin, bottom: thin, right: thin };
            const grey = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF2F2F2' } };

            // 25 columns: A-Y (Y is a trailing blank spacer)
            const colWidths = [
                5, 18, 13, 22, 22, 18, 24, 22, 13, 21.71,
                13, 13, 40, 36, 15, 14, 10, 15, 42,
                24, 22, 24, 22, 16, 8.71
            ];
            ws.columns = colWidths.map(w => ({ width: w }));

            // ── ROW 1: Sub-column headers ─────────────────────────────────────
            ws.getRow(1).height = 54.75;
            const subHeaders = [
                { col: 1, label: 'Sl.' },
                { col: 2, label: 'Lic. No.' },
                { col: 3, label: 'Date' },
                { col: 4, label: 'File No.' },
                { col: 5, label: 'CIF Value\nin Indian\nRupees' },
                { col: 6, label: 'CIF Value\nin USD' },
                { col: 7, label: 'Export\nObligation in\nIndian Rupees' },
                { col: 8, label: 'Export\nObligation\nin USD' },
                { col: 9, label: 'EOP Date' },
                { col: 10, label: 'Type of\nNorm' },
                { col: 11, label: 'CIF\nCurrency' },
                { col: 12, label: 'EO\nCurrency' },
                { col: 13, label: 'Add' },
                { col: 14, label: 'Name of\nCompany' },
                { col: 15, label: 'IEC no' },
                { col: 16, label: 'Pan\nNumber' },
                { col: 17, label: 'FY' },
                { col: 18, label: 'Application\ndate' },
                { col: 19, label: 'DGFT Name' },
                { col: 20, label: 'Total Export\nin Rs.' },
                { col: 21, label: 'Total Export\nin FCC' },
                { col: 22, label: 'CIF Value\nin Indian\nRupees' },
                { col: 23, label: 'CIF Value\nin USD' },
                { col: 24, label: 'Value\nAddition\n%' },
            ];
            subHeaders.forEach(({ col, label }) => {
                const cell = ws.getCell(1, col);
                cell.value = label;
                cell.font = { bold: true, size: 12, name: 'Calibri' };
                // Unlike every other sheet, the reference leaves this header row unfilled
                // (bold + bordered only, no grey band).
                cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
                cell.border = allBorders;
            });

            // ── ROWS 2+: Data rows ────────────────────────────────────────────
            masterSummaryData.forEach((row, rowIndex) => {
                const rNum = 2 + rowIndex;
                const dataRow = ws.getRow(rNum);
                dataRow.height = 60; // reference: row 2 (the data row) is 60.0

                const exportInr = Number(row['total_actual_export_inr']) || 0;
                const exportUsd = Number(row['total_actual_export_usd']) || 0;
                const importInr = Number(row['total_actual_import_inr']) || 0;
                const importUsd = Number(row['total_actual_import_usd']) || 0;

                // DGFT VA% = (Export USD - Import USD) / Import USD
                const vaPercent = importUsd > 0
                    ? ((exportUsd - importUsd) / importUsd)
                    : 0;

                const cellDefs = [
                    { col: 1, value: rowIndex + 1 },
                    { col: 2, value: row['lic_no'] ?? null },
                    { col: 3, value: row['lic_date'] ? dayjs(row['lic_date']).format('DD-MMM-YY') : null },
                    { col: 4, value: row['file_no'] ?? null },
                    { col: 5, value: Number(row['cif_allowed_inr']) || null, isNum: true },
                    { col: 6, value: Number(row['cif_allowed_usd']) || null, isNum: true },
                    { col: 7, value: Number(row['export_obligation_inr']) || null, isNum: true },
                    { col: 8, value: Number(row['export_obligation_usd']) || null, isNum: true },
                    { col: 9, value: row['eop_date'] ? dayjs(row['eop_date']).format('DD-MMM-YY') : null },
                    { col: 10, value: row['type_of_norm'] ?? null },
                    { col: 11, value: row['cif_currency'] ?? 'USD' },
                    { col: 12, value: row['eo_currency'] ?? 'USD' },
                    { col: 13, value: row['address'] ?? null, isLeft: true },
                    { col: 14, value: row['company_name'] ?? null, isLeft: true },
                    { col: 15, value: row['iec_no'] ?? null },
                    { col: 16, value: row['pan_number'] ?? null },
                    { col: 17, value: row['fy'] ?? null },
                    { col: 18, value: exportDate },
                    { col: 19, value: row['dgft_name'] ?? null, isLeft: true },
                    { col: 20, value: exportInr || null, isNum: true },
                    { col: 21, value: exportUsd || null, isNum: true },
                    { col: 22, value: importInr || null, isNum: true },
                    { col: 23, value: importUsd || null, isNum: true },
                    { col: 24, value: vaPercent, isNum: true, isPercent: true },
                ];

                cellDefs.forEach(({ col, value, isNum, isLeft, isPercent: isPct }) => {
                    const cell = dataRow.getCell(col);
                    cell.value = value;
                    cell.font = { size: 12, name: 'Calibri' };
                    cell.border = allBorders;
                    if (isNum && typeof value === 'number') {
                        cell.numFmt = isPct ? '0.00%' : '#,##0.00;(#,##0.00);"-"';
                        cell.alignment = { vertical: 'middle', wrapText: true, horizontal: 'right' };
                    } else {
                        cell.alignment = { vertical: 'middle', wrapText: true, horizontal: isLeft ? 'left' : 'center' };
                    }
                });
            });
        };
        // ── Export Ledger sheet ────────────────────────────────────────────────
        const addExportLedgerSheet = () => {
            if (!exportLedgerData || exportLedgerData.length === 0) return;
            const ws = workbook.addWorksheet('Export Ledger');

            ws.pageSetup = {
                paperSize: 9,
                orientation: 'portrait',
                margins: { left: 0.7, right: 0.7, top: 0.75, bottom: 0.75, header: 0, footer: 0 }
            };

            const colWidths = [12, 18, 16, 12, 18, 16, 20, 22, 18, 22, 24, 18, 14, 14, 14, 16, 16, 16, 18, 20, 20, 20, 22, 20, 20, 8.71];
            ws.columns = colWidths.map(w => ({ width: w }));

            const thin = { style: 'thin' };
            const allBorders = { top: thin, left: thin, bottom: thin, right: thin };

            const getColLetter = (n) => {
                let s = '';
                while (n > 0) {
                    let m = (n - 1) % 26;
                    s = String.fromCharCode(65 + m) + s;
                    n = Math.floor((n - m) / 26);
                }
                return s;
            };

            const colLetters = Array.from({ length: 25 }, (_, i) => getColLetter(i + 1));

            ws.getRow(1).height = 24.75;
            setMergedCell(ws, 'A1:Y1', 'Export Ledger', { bold: true, fontSize: 12, horizontal: 'left' });

            // Row 2 (headers): reference leaves this at the default row height, unlike every
            // other sheet's tall header row — no explicit height override here.
            const headersRow2 = [
                'Export Sr No',
                'Export Qty As per AA',
                'Actual Export Qty',
                'Import Sr No',
                'Import Qty As per AA',
                'Actual Import Qty',
                'Actual CIF Value (in INR)',
                'Export Qty Proportionate of Actual Import',
                'Export Balance Quantity',
                'Import Qty required for Bal Export',
                'Import CIF Value required for Bal Export',
                'Assessable Value',
                'BCD % Duty Rate',
                'SWS % Duty Rate',
                'IGST % Duty Rate',
                'BCD Exempted',
                'SWS Exempted',
                'IGST Exempted',
                'Total Duty Exempted',
                'BCD Payable on Excess Imports',
                'SWS Payable on Excess Imports',
                'IGST Payable on Excess Imports',
                'Total Duty on Bal Export in Rs.',
                'Actual Export FOB in FCC',
                'Realisation in FCC'
            ];

            headersRow2.forEach((hdr, i) => {
                const cell = ws.getCell(`${colLetters[i]}2`);
                cell.value = hdr;
                cell.font = { bold: true, size: 12 };
                cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
                cell.border = allBorders;
            });

            exportLedgerData.forEach((row, idx) => {
                const rowNum = idx + 3;
                const excelRow = ws.getRow(rowNum);
                excelRow.height = 39.75;

                const expSrNo = Number(row.export_sr_no) || 1;
                const expQtyAa = row.exp_qty_as_per_auth != null ? Number(row.exp_qty_as_per_auth) : 0;
                const actExpQty = row.actual_export_qty != null ? Number(row.actual_export_qty) : 0;
                const impSrNo = Number(row.import_item_sr_no) || 1;
                const impQtyAa = row.imp_qty_as_per_lic != null ? Number(row.imp_qty_as_per_lic) : 0;
                const actImpQty = row.actual_imports_made != null ? Number(row.actual_imports_made) : 0;
                const actCifInr = row.actual_cif_inr != null ? Number(row.actual_cif_inr) : 0;

                const bcdRate = row.bcd_rate != null ? Number(row.bcd_rate) : 15;
                const swsRate = row.sws_rate != null ? Number(row.sws_rate) : 10;
                const igstRate = row.igst_rate != null ? Number(row.igst_rate) : 18;

                const propExpQty = row.prop_exp_qty != null ? Number(row.prop_exp_qty) : (impQtyAa > 0 ? (expQtyAa / impQtyAa) * actImpQty : 0);
                const expBalQty = row.exp_bal_qty != null ? Number(row.exp_bal_qty) : (propExpQty - actExpQty);
                const impQtyReqBal = row.imp_qty_req_bal != null ? Number(row.imp_qty_req_bal) : (expQtyAa > 0 ? (impQtyAa / expQtyAa) * expBalQty : 0);
                const impCifReqBal = row.imp_cif_req_bal != null ? Number(row.imp_cif_req_bal) : (actImpQty > 0 ? (actCifInr / actImpQty) * impQtyReqBal : 0);

                const bcdExempted = row.bcd_exempted != null ? Number(row.bcd_exempted) : (actCifInr * (bcdRate / 100));
                const swsExempted = row.sws_exempted != null ? Number(row.sws_exempted) : (bcdExempted * (swsRate / 100));
                const igstExempted = row.igst_exempted != null ? Number(row.igst_exempted) : ((actCifInr + bcdExempted + swsExempted) * (igstRate / 100));
                const totalDutyExempted = row.total_duty_exempted != null ? Number(row.total_duty_exempted) : (bcdExempted + swsExempted + igstExempted);

                const bcdPayableExcess = row.bcd_payable_excess != null ? Number(row.bcd_payable_excess) : (actImpQty > 0 ? (bcdExempted / actImpQty) * impQtyReqBal : 0);
                const swsPayableExcess = row.sws_payable_excess != null ? Number(row.sws_payable_excess) : (actImpQty > 0 ? (swsExempted / actImpQty) * impQtyReqBal : 0);
                const igstPayableExcess = row.igst_payable_excess != null ? Number(row.igst_payable_excess) : (actImpQty > 0 ? (igstExempted / actImpQty) * impQtyReqBal : 0);
                const totalDutyBalExp = row.total_duty_bal_exp != null ? Number(row.total_duty_bal_exp) : (bcdPayableExcess + swsPayableExcess + igstPayableExcess);

                const actExpFobFc = row.actual_export_fob_fc != null ? Number(row.actual_export_fob_fc) : null;
                const realisationFc = row.realisation_fc != null ? Number(row.realisation_fc) : null;

                excelRow.getCell(1).value = expSrNo;
                excelRow.getCell(2).value = expQtyAa;
                excelRow.getCell(3).value = actExpQty;
                excelRow.getCell(4).value = impSrNo;
                excelRow.getCell(5).value = impQtyAa;
                excelRow.getCell(6).value = actImpQty !== 0 ? actImpQty : null;
                excelRow.getCell(7).value = actCifInr !== 0 ? actCifInr : null;
                excelRow.getCell(8).value = propExpQty !== 0 ? propExpQty : null;
                excelRow.getCell(9).value = expBalQty !== 0 ? expBalQty : null;
                excelRow.getCell(10).value = impQtyReqBal !== 0 ? impQtyReqBal : null;
                excelRow.getCell(11).value = actImpQty > 0 ? impCifReqBal : null;
                excelRow.getCell(12).value = actCifInr !== 0 ? actCifInr : null;
                // Excel percentage cells store decimal fractions: 15 => 0.15 => 15.00%.
                excelRow.getCell(13).value = bcdRate / 100;
                excelRow.getCell(14).value = swsRate / 100;
                excelRow.getCell(15).value = igstRate / 100;
                excelRow.getCell(16).value = bcdExempted !== 0 ? bcdExempted : null;
                excelRow.getCell(17).value = swsExempted !== 0 ? swsExempted : null;
                excelRow.getCell(18).value = igstExempted !== 0 ? igstExempted : null;
                excelRow.getCell(19).value = totalDutyExempted !== 0 ? totalDutyExempted : null;
                excelRow.getCell(20).value = actImpQty > 0 ? (bcdPayableExcess !== 0 ? bcdPayableExcess : null) : null;
                excelRow.getCell(21).value = actImpQty > 0 ? (swsPayableExcess !== 0 ? swsPayableExcess : null) : null;
                excelRow.getCell(22).value = actImpQty > 0 ? (igstPayableExcess !== 0 ? igstPayableExcess : null) : null;
                excelRow.getCell(23).value = actImpQty > 0 ? (totalDutyBalExp !== 0 ? totalDutyBalExp : null) : null;
                excelRow.getCell(24).value = actExpFobFc;
                excelRow.getCell(25).value = realisationFc;

                colLetters.forEach((col, i) => {
                    const cell = excelRow.getCell(i + 1);
                    cell.border = allBorders;
                    if ([0, 3].includes(i)) {
                        cell.alignment = { horizontal: 'center', vertical: 'middle' };
                    } else {
                        cell.alignment = { horizontal: 'right', vertical: 'middle' };
                        if (typeof cell.value === 'number') {
                            // Columns B, C, E, F, G, H (Export Qty As per AA / Actual Export Qty /
                            // Import Qty As per AA / Actual Import Qty / Actual CIF Value in INR /
                            // Export Qty Proportionate of Actual Import) use the accounting format
                            // in the reference; every other numeric column here uses the plain
                            // format — this inconsistency is in the reference itself.
                            if ([1, 2, 4, 5, 6, 7].includes(i)) {
                                cell.numFmt = '_(* #,##0.00_);_(* \\(#,##0.00\\);_(* "-"??_);_(@_)';
                            } else {
                                cell.numFmt = [12, 13, 14].includes(i)
                                    ? '0.00%'
                                    : '#,##0.00;(#,##0.00);"-"';
                            }
                        }
                    }
                });
            });
        };

        addDatabaseSheet();
        addImportLedgerSheet();
        addExportLedgerSheet();

        applyRdnCalibri12(workbook);
        const buffer = await workbook.xlsx.writeBuffer();
        // ExcelJS's `ws.autoFilter` only writes the worksheet-level <autoFilter> element — Excel
        // and LibreOffice also need the companion _xlnm._FilterDatabase defined name to actually
        // render the dropdown buttons (see addFilterDatabaseName).
        const blob = importStFilterRange
            ? await addFilterDatabaseName(buffer, 'Import St', importStFilterRange)
            : new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(blob, `Adv_Auth_RDN_Lic_${getTodaysDate()}_${selectedIEC}.xlsx`);
    };

    const exportAnnexureToExcel = async () => {
        if (!billNo) {
            message.error("Please select an Auth Number to download the Annexure.");
            return;
        }

        let workbook = new ExcelJS.Workbook();

        let annexureSheet = workbook.addWorksheet('Certificate');

        annexureSheet.pageSetup = {
            paperSize: 9,
            orientation: 'portrait',
            fitToPage: true,
            fitToWidth: 1,
            fitToHeight: 0,
            margins: { left: 0.25, right: 0.25, top: 0.5, bottom: 0.5, header: 0.2, footer: 0.2 }
        };

        // Reference's <cols> XML stores these as multi-column ranges (A:B=14,
        // C:D=25.71, E:G=15) — every column is explicitly set, none are left at
        // Excel's default. Table B's headers (row 18) use these columns
        // un-merged, so getting them right matters for that grid's proportions.
        const certColWidths = [14, 14, 25.71, 25.71, 15, 15, 15];
        annexureSheet.columns = certColWidths.map(w => ({ width: w }));
        certColWidths.forEach((w, idx) => {
            annexureSheet.getColumn(idx + 1).width = w;
        });

        const applyBorderToRange = (sheet, startRow, endRow, startCol, endCol) => {
            for (let r = startRow; r <= endRow; r++) {
                for (let c = startCol; c <= endCol; c++) {
                    let cell = sheet.getCell(r, c);
                    cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
                }
            }
        };

        const addMergedTitle = (sheet, range, text, bold = true, horizontal = 'center') => {
            sheet.mergeCells(range);
            let cell = sheet.getCell(range.split(':')[0]);
            cell.value = text;
            cell.font = { bold };
            cell.alignment = { horizontal };
        };

        addMergedTitle(annexureSheet, 'A1:G1', 'Annexure:');
        addMergedTitle(annexureSheet, 'A2:G2', 'Format of Accountability statement for grant of EODC');
        addMergedTitle(annexureSheet, 'A3:G3', '(For cases covered under sl. No-4 of general notes for engineering products and Sl No-6');
        addMergedTitle(annexureSheet, 'A4:G4', 'of General Notes for All Export Products Groups)');

        annexureSheet.addRow([]);

        const licData = masterSummaryData.find(
            item => String(item.lic_no || '').trim() === String(billNo || '').trim()
        ) || masterSummaryData[0] || {};

        addMergedTitle(annexureSheet, 'A6:B6', 'Name of the applicant:', true, 'left');
        addMergedTitle(annexureSheet, 'C6:G6', selectedIECName || '', false, 'left');

        addMergedTitle(annexureSheet, 'A7:B7', 'IEC number:', true, 'left');
        addMergedTitle(annexureSheet, 'C7:G7', selectedIEC || '', false, 'left');

        addMergedTitle(annexureSheet, 'A8:B8', 'Advance Authorisation No:', true, 'left');
        annexureSheet.getCell('C8').value = billNo;
        // Client reference uses dotted DD.MM.YYYY specifically for this date field.
        addMergedTitle(annexureSheet, 'E8:G8', 'Date of issue: ' + (licData.lic_date ? dayjs(licData.lic_date).format('DD.MM.YYYY') : ''), false, 'left');

        annexureSheet.addRow([]);

        addMergedTitle(annexureSheet, 'A10:G10', 'Table A : Export item details:', true, 'left');

        annexureSheet.mergeCells('A12:A12');
        annexureSheet.getCell('A12').value = 'Sl. No of export Item';
        annexureSheet.mergeCells('B12:D12');
        annexureSheet.getCell('B12').value = 'Export Item Description';
        annexureSheet.mergeCells('E12:F12');
        annexureSheet.getCell('E12').value = 'Total Qty exported';
        annexureSheet.mergeCells('G12:G12');
        annexureSheet.getCell('G12').value = 'UOM';

        annexureSheet.mergeCells('A13:A13');
        annexureSheet.getCell('A13').value = '(1)';
        annexureSheet.mergeCells('B13:D13');
        annexureSheet.getCell('B13').value = '(2)';
        annexureSheet.mergeCells('E13:F13');
        annexureSheet.getCell('E13').value = '(3)';
        annexureSheet.mergeCells('G13:G13');
        annexureSheet.getCell('G13').value = '(4)';

        annexureSheet.getRow(12).height = 40;
        applyBorderToRange(annexureSheet, 12, 13, 1, 7);
        [12, 13].forEach(rowNum => {
            for (let c = 1; c <= 7; c++) {
                let cell = annexureSheet.getCell(rowNum, c);
                cell.font = { bold: true };
                cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
            }
        });

        // Table A: quantity comes from Export Statement.
        // UOM extraction helper to always display the standard short code (e.g. "NOS", "MTS").
        const cleanUom = (u) => {
            if (!u) return '';
            const m = String(u).match(/\(([^)]+)\)/);
            return m ? m[1].trim() : String(u).trim();
        };

        const ACC_FMT = '_(* #,##0.00_);_(* (#,##0.00);_(* "-"??_);_(@_)';
        const tableAAuthFieldsBySr = {};
        shbWiseData
            .filter(row => !billNo || String(row.auth_number || '').trim() === String(billNo || '').trim())
            .forEach(row => {
                if (!row.exp_sr_no || tableAAuthFieldsBySr[row.exp_sr_no]) return;
                if (row.exp_description || row.exp_uom) {
                    tableAAuthFieldsBySr[row.exp_sr_no] = {
                        desc: row.exp_description || '',
                        uom: row.exp_uom || '',
                    };
                }
            });

        const tableARowsMap = {};
        exportStData
            .filter(row => !billNo || String(row.auth_number || '').trim() === String(billNo || '').trim())
            .forEach(row => {
                if (!row.export_sr_no) return;
                const key = row.export_sr_no;
                const authFields = tableAAuthFieldsBySr[row.export_sr_no];
                if (!tableARowsMap[key]) {
                    tableARowsMap[key] = {
                        slNo: row.export_sr_no,
                        desc: authFields?.desc || row.product_exported || '',
                        totalQty: 0,
                        uom: cleanUom(row.uom) || cleanUom(authFields?.uom) || '',
                    };
                }
                tableARowsMap[key].totalQty += Number(row.export_qty) || 0;
            });
        const tableAData = Object.values(tableARowsMap).sort((a, b) => sortBySrNo(a, b, 'slNo'));

        let currentRow = 14;
        tableAData.forEach(item => {
            const row = annexureSheet.getRow(currentRow);
            annexureSheet.mergeCells(`A${currentRow}:A${currentRow}`);
            annexureSheet.getCell(`A${currentRow}`).value = item.slNo;
            annexureSheet.mergeCells(`B${currentRow}:D${currentRow}`);
            annexureSheet.getCell(`B${currentRow}`).value = item.desc;
            annexureSheet.mergeCells(`E${currentRow}:F${currentRow}`);
            const qtyCell = annexureSheet.getCell(`E${currentRow}`);
            qtyCell.value = Number(item.totalQty) || 0;
            qtyCell.numFmt = ACC_FMT;
            annexureSheet.mergeCells(`G${currentRow}:G${currentRow}`);
            annexureSheet.getCell(`G${currentRow}`).value = item.uom;

            applyBorderToRange(annexureSheet, currentRow, currentRow, 1, 7);

            // Merged B:D width is ~65.42 character-units (14 + 25.71 + 25.71).
            const lines = getLines(item.desc, 65);
            row.height = Math.max(24, lines * 15);

            for (let c = 1; c <= 7; c++) {
                annexureSheet.getCell(currentRow, c).alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
            }
            // Description column (B:D) is left-aligned; Sl.No/Qty/UOM stay centered.
            annexureSheet.getCell(currentRow, 2).alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
            currentRow++;
        });

        currentRow++;

        addMergedTitle(annexureSheet, `A${currentRow}:D${currentRow}`, 'Table B: Import Item details', true, 'left');
        currentRow += 2;

        const tableBHeaders = [
            'Sl. No.of export item (as per table A)',
            'Corresponding Sl. No of input as per authorisation',
            'Description of input as per bill of entry (including technical specification/part nos etc.)',
            'Description of input actually used in the resultant product. (including technical specification /part nos etc.)',
            'Total qty imported as per B/E for qty exported as per column (3) of table A',
            'Qty consumed in the resultant product for qty exported as per column (3) of table A',
            'Excess Import Qty'
        ];

        annexureSheet.getRow(currentRow).values = tableBHeaders;
        annexureSheet.getRow(currentRow + 1).values = ['(1)', '(2)', '(3)', '(4)', '(5)', '(6)', '(7)'];

        // Reference hardcodes this header row to 116.25 (the real columns — A/B/D/F/G
        // at the ~8.43 default width — force this much wrap on the longer headers).
        annexureSheet.getRow(currentRow).height = 116.25;

        [currentRow, currentRow + 1].forEach(rowNum => {
            annexureSheet.getRow(rowNum).eachCell(cell => {
                cell.font = { bold: true };
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
                cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
            });
        });

        currentRow += 2;
        let emptyTableBRow = annexureSheet.getRow(currentRow);
        emptyTableBRow.values = ['', '', '', '', '', '', ''];
        emptyTableBRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
            if (colNumber <= 7) {
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            }
        });

        addMergedTitle(annexureSheet, `A${currentRow}:G${currentRow}`, 'Annexure - I Enclosed', true, 'center');
        currentRow += 2;

        addMergedTitle(annexureSheet, `A${currentRow}:G${currentRow}`, 'Note: Contents in column (3) and (4) as well as in column (5) and (6) should be same for grant of EODC.', false, 'left');
        currentRow += 2;

        addMergedTitle(annexureSheet, `A${currentRow}:G${currentRow}`, 'Declaration', true, 'center');
        currentRow += 2;

        const declTextVal = 'I/We hereby declare that information furnished as above are true and correct as per records maintained by us. Information furnished above if found to be incorrect or false or misleading in future, I/We shall be liable for penal action under the provisions of FTDR Act and rules made thereunder.';
        addMergedTitle(annexureSheet, `A${currentRow}:G${currentRow}`, declTextVal, false, 'left');
        const declLines = getLines(declTextVal, 130);
        annexureSheet.getRow(currentRow).height = Math.max(40, declLines * 16);
        annexureSheet.getCell(`A${currentRow}`).alignment = { wrapText: true, vertical: 'middle', horizontal: 'left' };

        currentRow += 3;

        addMergedTitle(annexureSheet, `A${currentRow}:B${currentRow}`, 'Place: Pune', false, 'left');
        addMergedTitle(annexureSheet, `E${currentRow}:G${currentRow}`, 'Signature of Applicant', false, 'left');
        currentRow++;

        addMergedTitle(annexureSheet, `A${currentRow}:B${currentRow}`, `Date: ${formattedDate}`, false, 'left');
        addMergedTitle(annexureSheet, `E${currentRow}:G${currentRow}`, 'Name:', false, 'left');
        currentRow++;
        addMergedTitle(annexureSheet, `E${currentRow}:G${currentRow}`, 'Designation:', false, 'left');
        currentRow++;
        addMergedTitle(annexureSheet, `E${currentRow}:G${currentRow}`, 'Telephone Number:', false, 'left');
        currentRow++;
        addMergedTitle(annexureSheet, `E${currentRow}:G${currentRow}`, 'Email address:', false, 'left');

        annexureSheet.columns = certColWidths.map(w => ({ width: w }));
        // Client reference does not box the whole certificate page — only the
        // Table A / Table B grids carry borders (already applied above).

        let annexure1Sheet = workbook.addWorksheet('ANNEXURE-I');

        annexure1Sheet.pageSetup = {
            paperSize: 9,
            orientation: 'landscape',
            fitToPage: true,
            fitToWidth: 1,
            fitToHeight: 0,
            margins: { left: 0.25, right: 0.25, top: 0.5, bottom: 0.5, header: 0.2, footer: 0.2 }
        };

        addMergedTitle(annexure1Sheet, 'A1:I1', 'ANNEXURE-I', true, 'left');
        addMergedTitle(annexure1Sheet, 'A2:I2', 'Table B: Import Item details', true, 'left');

        annexure1Sheet.mergeCells('A4:A5');
        annexure1Sheet.getCell('A4').value = 'Sl. No.of export item (as per table A)';

        annexure1Sheet.mergeCells('B4:B5');
        annexure1Sheet.getCell('B4').value = 'Corresponding Sl. No of input as per authorisation';

        annexure1Sheet.mergeCells('C4:C5');
        annexure1Sheet.getCell('C4').value = 'Description of input as per bill of entry (including technical specification/part nos etc.)';

        annexure1Sheet.mergeCells('D4:D5');
        annexure1Sheet.getCell('D4').value = 'Description of input actually used in the resultant product. (including technical specification /part nos etc.)';

        annexure1Sheet.mergeCells('E4:F4');
        annexure1Sheet.getCell('E4').value = 'Total qty imported as per B/E for qty exported as per column (3) of table A';
        annexure1Sheet.getCell('E5').value = 'QTY';
        annexure1Sheet.getCell('F5').value = 'UOM';

        annexure1Sheet.mergeCells('G4:H4');
        annexure1Sheet.getCell('G4').value = 'Qty consumed in the resultant product for qty exported as per column (3) of table A';
        annexure1Sheet.getCell('G5').value = 'QTY';
        annexure1Sheet.getCell('H5').value = 'UOM';

        annexure1Sheet.mergeCells('I4:I5');
        annexure1Sheet.getCell('I4').value = 'Excess Import Qty';

        annexure1Sheet.getRow(6).values = ['(1)', '(2)', '(3)', '(4)', '(5)', '', '(6)', '', '(7)'];

        annexure1Sheet.getRow(4).height = 75.75;
        annexure1Sheet.getRow(5).height = 35.1;

        applyBorderToRange(annexure1Sheet, 4, 6, 1, 9);

        [4, 5, 6].forEach(rowNum => {
            annexure1Sheet.getRow(rowNum).eachCell({ includeEmpty: true }, cell => {
                cell.font = { bold: true };
                cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
            });
        });

        // Annexure-I is a summary register at export/input-pair grain. A single
        // export item can consume multiple authorised inputs, so retain one row
        // for every (export serial, import serial) pair while still collapsing
        // repeated SHB/reference rows within that pair.
        const annexure1SummaryByExportImportSr = new Map();
        shbWiseData
            .filter(row => !billNo || String(row.auth_number || '').trim() === String(billNo || '').trim())
            .forEach(item => {
                const expSrKey = String(item.exp_sr_no ?? '').trim();
                const importSrKey = String(item.import_item_sr_no ?? '').trim();
                if (!expSrKey) return;
                const key = JSON.stringify([expSrKey, importSrKey]);
                if (!annexure1SummaryByExportImportSr.has(key)) {
                    annexure1SummaryByExportImportSr.set(key, {
                        expSrNo: item.exp_sr_no,
                        importSrNo: item.import_item_sr_no,
                        inputDescription: item.import_item_description || '',
                        inputUom: item.imp_uom || item.actual_import_uom || '',
                        actualImportQty: 0,
                        qtyConsumed: 0,
                    });
                }
                const summary = annexure1SummaryByExportImportSr.get(key);
                // actual_imports_made is repeated group-level data — take MAX, not SUM.
                summary.actualImportQty = Math.max(summary.actualImportQty, Number(item.actual_imports_made) || 0);
                summary.qtyConsumed += Number(item.qty_used) || 0;
            });

        const annexure1Rows = Array.from(annexure1SummaryByExportImportSr.values())
            .sort((a, b) => {
                const exportOrder = sortBySrNo(a, b, 'expSrNo');
                return exportOrder || sortBySrNo(a, b, 'importSrNo');
            });

        let cRow1 = 8;
        annexure1Rows.forEach(item => {
            const row = annexure1Sheet.getRow(cRow1);
            row.values = [
                item.expSrNo,
                item.importSrNo,
                item.inputDescription,
                item.inputDescription,
                item.actualImportQty,
                item.inputUom,
                item.qtyConsumed,
                item.inputUom,
                { formula: `E${cRow1}-G${cRow1}` }, // live "Excess Import Qty" formula, matching the client file
            ];

            const colWidths = [12, 15, 30.86, 30.71, 12, 10, 12, 10, 10.14];
            let maxLines = 1;
            [item.expSrNo, item.importSrNo, item.inputDescription, item.inputDescription, item.actualImportQty, item.inputUom, item.qtyConsumed, item.inputUom]
                .forEach((val, i) => {
                    const lines = getLines(val !== undefined && val !== null ? String(val) : '', colWidths[i] || 20);
                    if (lines > maxLines) maxLines = lines;
                });
            row.height = Math.max(24, maxLines * 16);

            row.eachCell(cell => {
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
                cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
            });
            // Reference left-aligns the two description columns (C, D) — centering
            // long wrapped text in a ~30-char-wide column looks visually wrong.
            [3, 4].forEach(c => {
                annexure1Sheet.getCell(cRow1, c).alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
            });
            [5, 7, 9].forEach(c => { annexure1Sheet.getCell(cRow1, c).numFmt = ACC_FMT; });

            cRow1++;
        });

        annexure1Sheet.columns = [
            { width: 12 }, { width: 15 }, { width: 30.86 }, { width: 30.71 },
            { width: 12 }, { width: 10 }, { width: 12 }, { width: 10 }, { width: 10.14 }
        ];
        // Client reference does not box the whole sheet — only the header block
        // (bordered above) and each data row (bordered in the loop above) carry
        // borders; the title rows stay unboxed.

        applyCalibri12(workbook);
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(blob, `Accountablity_St_Cert_Annexure_I_${billNo}_${getTodaysDate()}.xlsx`);
    };

    const exportCoveringLetterExcel = async () => {
        if (!billNo) return;
        const licData = masterSummaryData.find(
            item => String(item.lic_no || '').trim() === String(billNo || '').trim()
        );
        const recipientAddress = cleanAddress(licData?.registered_address) || cleanAddress(licData?.address);
        if (!recipientAddress) {
            message.error(`No licensee address is available for licence ${billNo}.`);
            return;
        }
        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet('Covering_Letter');

        // Client reference (LEO-Covering_Letter): plain letter, content in column B
        // spanning B:E, A is a narrow gutter. Calibri 13 (enclosure list 12), no
        // borders, no fills, portrait A4 at 84% print scale.
        sheet.pageSetup = {
            paperSize: 9,
            orientation: 'portrait',
            scale: 84,
            margins: { left: 0.55, right: 0.31, top: 0.75, bottom: 0.75, header: 0.3, footer: 0.3 },
        };
        sheet.views = [{ state: 'normal', showGridLines: false }];
        sheet.columns = [{ width: 3 }, { width: 27.86 }, { width: 27.43 }, { width: 35 }, { width: 21.14 }];

        const FS = 13;
        const LIST_FS = 12;
        const WRAP_W = 108;
        const licDateDotted = licData.lic_date ? dayjs(licData.lic_date).format('DD.MM.YYYY') : '';
        const signDate = dayjs().format('DD.MM.YYYY');
        // Client writes "KMPL" (company acronym) here, not the IEC number.
        const companyAcronym = String(selectedIECName || '')
            .split(/\s+/).filter(w => /[A-Za-z]/.test(w)).map(w => w[0].toUpperCase()).join('')
            || selectedIEC || '';

        const line = (rowNum, text, opts = {}) => {
            const { bold = false, size = FS, horizontal = 'left', wrapText = false, merge = false, minHeight = 0 } = opts;
            if (merge) { try { sheet.mergeCells(`B${rowNum}:E${rowNum}`); } catch (e) { /* already merged */ } }
            const cell = sheet.getCell(`B${rowNum}`);
            cell.value = text;
            cell.font = { name: 'Calibri', size, bold };
            cell.alignment = { horizontal, vertical: 'middle', wrapText };
            const h = Math.max(minHeight, wrapText ? getLines(text, WRAP_W) * 16 : 0);
            if (h) sheet.getRow(rowNum).height = h;
        };

        line(6, `Ref. No. :- ${companyAcronym}/Adv. Rdn/${billNo}`, { bold: true });
        const dateCell = sheet.getCell('E6');
        dateCell.value = `Date: ${signDate}`;
        dateCell.font = { name: 'Calibri', size: FS, bold: true };

        line(8, 'To,');
        line(9, licData.company_name || selectedIECName, { merge: true, wrapText: true });
        line(10, recipientAddress, { merge: true, wrapText: true });

         line(14, 'Subject :- Application for Redemption of Advance Authorization', { bold: true, merge: true });
        line(16, `Advance Authorization No.${billNo} Dt.${licDateDotted}`, { bold: true, merge: true });
        line(18, `Your Office File No. :- ${licData.file_no || ''}`, { bold: true, merge: true });

        line(20, 'Dear Sir,');

        line(22, 'Please find enclosed herewith application in ANF-4F for Redemption of above-mentioned Advance License.', { merge: true, wrapText: true, minHeight: 18 });
        line(24, 'To enable you to consider our application and issue the necessary Export Obligation Discharge Letter at the earliest we enclose the following: -', { bold: true, merge: true, wrapText: true, minHeight: 34 });

        [
            '1 Application in prescribed format ANF 4F',
            '2. Copy of Advance License',
            '3. Copies of Shipping bills',
            '4. Bankers Certificate of realization (e-BRC)',
            '5. A Statement of Imports duly attested by CA',
            '6. A Statement of Exports duly attested by CA',
            '7. Appendix 4 H duly attested by CA',
            '8. Accountability Certificate with attested by CE (Chartered Engineer) as per Policy circular no.10/2018-19 dated.13.07.2018',
        ].forEach((text, i) => line(26 + i, text, { size: LIST_FS, merge: true, wrapText: true, minHeight: 16 }));

        line(36, 'Declaration as per P.N.21 Dt. 09.10.2012', { bold: true, horizontal: 'center', merge: true, minHeight: 20 });
        line(38, 'We hereby declare that Freight, Insurance and Commission values as mentioned in the application are based on actual transaction values. For the purpose of calculating entitlement, commission amount has been excluded.', { merge: true, wrapText: true, minHeight: 18 });
        // The character-count wrap estimate overshoots this particular line (it
        // actually wraps to 2 lines at this merged width, not 3) — force the
        // reference's exact height instead of trusting the heuristic.
        sheet.getRow(38).height = 32;

        line(41, 'We now request you to issue Export Obligation Discharge Letter at the earliest.', { merge: true });
        line(43, 'Thanking You.');
        line(44, 'Yours Faithfully,');
        line(46, `For ${selectedIECName}`, { bold: true, merge: true });
        line(49, 'Name: -', { bold: true });
        line(50, 'Designation: -', { bold: true });

        applyCalibri12(workbook);
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `Covering_Letter_${billNo}_${getTodaysDate()}.xlsx`);
    };

    const exportAnf4FExcel = async () => {
        if (!billNo) {
            message.error("Please select an Auth Number to download ANF-4F.");
            return;
        }

        const licData = masterSummaryData.find(
            item => String(item.lic_no || '').trim() === String(billNo || '').trim()
        ) || masterSummaryData[0] || {};

        let workbook = new ExcelJS.Workbook();
        let sheet = workbook.addWorksheet('ANF-4F ');

        // Client reference (LEO- ANF_4F): single portrait A4 form, content confined
        // to columns B:L, printed at 69% scale with 4 pages tall, medium outer rule
        // + thin inner grid, gridlines shown (not hidden).
        sheet.pageSetup = {
            paperSize: 9,
            orientation: 'portrait',
            scale: 69,
            fitToHeight: 4,
            margins: { left: 0.23, right: 0.2, top: 0.43, bottom: 0.51, header: 0.31, footer: 0.31 }
        };
        sheet.pageSetup.printArea = 'B1:L89';

        sheet.columns = [
            { width: 4 },      // A – left gutter
            { width: 10 },     // B
            { width: 11.71 },  // C
            { width: 8.43 },   // D – reference leaves this column unset, which computes to ~8.43
                               // (Excel's default for baseColWidth=8); setting it explicitly instead
                               // of leaving it unset, because an unset width renders inconsistently
                               // (collapses near-zero) in LibreOffice/other viewers.
            { width: 12 },     // E
            { width: 12.14 },  // F
            { width: 11.29 },  // G
            { width: 11.29 },  // H
            { width: 16.71 },  // I
            { width: 12.57 },  // J
            { width: 11.29 },  // K
            { width: 16.29 },  // L
        ];

        const FS = 12;
        const thin = { style: 'thin' };
        const ACC_FMT = '_(* #,##0.00_);_(* (#,##0.00);_(* "-"??_);_(@_)';

        const setCell = (coord, value, opts = {}) => {
            const {
                bold = false, size = FS, horizontal = 'left', vertical = 'middle',
                wrapText = false, numFmt = null, underline = false,
            } = opts;
            const cell = sheet.getCell(coord);
            cell.value = value;
            cell.font = { name: 'Calibri', size, bold, underline };
            cell.alignment = { horizontal, vertical, wrapText };
            if (numFmt) cell.numFmt = numFmt;
            return cell;
        };
        const merge = range => {
            const p = range.split(':');
            if (p.length === 2 && p[0] !== p[1]) {
                try { sheet.mergeCells(range); } catch (e) { /* already merged */ }
            }
        };
        const mc = (range, value, opts = {}) => {
            merge(range);
            return setCell(range.split(':')[0], value, opts);
        };
        // Thin grid over a rectangle (also closes merged cells inside it).
        const gridBorder = (r1, r2, c1, c2) => {
            for (let r = r1; r <= r2; r++) {
                for (let c = c1; c <= c2; c++) {
                    sheet.getCell(r, c).border = { top: thin, left: thin, bottom: thin, right: thin };
                }
            }
        };
        const edgeBorder = (r1, r2, c1, c2, side, style) => {
            for (let r = r1; r <= r2; r++) {
                for (let c = c1; c <= c2; c++) {
                    const cell = sheet.getCell(r, c);
                    cell.border = { ...cell.border, [side]: { style } };
                }
            }
        };

        const licDate = licData.lic_date ? dayjs(licData.lic_date).format('DD.MM.YYYY') : '';
        const eopDate = licData.eop_date ? dayjs(licData.eop_date).format('DD.MM.YYYY') : '';
        const signDate = dayjs().format('DD.MM.YYYY');
        const num = v => (v != null && v !== '' && !isNaN(Number(v)) ? Number(v) : '');

        // ── Header ────────────────────────────────────────────────────────────
        sheet.getRow(1).height = 15.75;
        sheet.getRow(2).height = 15.75;
        sheet.getRow(3).height = 15.75;
        // Row 4 is left at the sheet default — the guideline text fits on one
        // line in this wide a merge, matching the reference.
        mc('B2:L2', 'ANF- 4F', { bold: true, horizontal: 'center' });
        mc('B3:L3', 'Application for Redemption / No Bond Certificate against Advance Authorisation', { bold: true, horizontal: 'center', wrapText: true });
        mc('B4:L4', '(Please see guidelines given at the end before filling the application online)', { bold: true, size: 11, horizontal: 'center', wrapText: true });
        edgeBorder(4, 4, 2, 12, 'bottom', 'thin');

        // ── 1. IEC ───────────────────────────────────────────────────────────
        sheet.getRow(5).height = 15.75;
        // Reference does NOT merge this label across B:C — single unmerged cell.
        setCell('B5', '1. IEC No. ', { bold: true });
        mc('D5:E5', selectedIEC || '', { horizontal: 'center' });
        setCell('H5', 'Branch Code', { bold: true });
        setCell('J5', 0, { horizontal: 'center' });
        edgeBorder(5, 5, 2, 12, 'top', 'thin');
        edgeBorder(5, 5, 2, 12, 'bottom', 'thin');
        edgeBorder(5, 5, 3, 3, 'right', 'thin');
        edgeBorder(5, 5, 7, 7, 'right', 'thin');

        sheet.getRow(6).height = 9.95;
        sheet.getRow(7).height = 15.75;
        sheet.getRow(8).height = 15.75;
        // Reference leaves these section-label rows unmerged too (single B cell,
        // text overflows rightward over the empty neighbors).
        setCell('B7', '2. Request for:   Redemption (EODC) / No Bond Certificate (Bond Waiver) ', { bold: false });
        setCell('B8', '3. (Strike out whichever is not applicable)', { bold: false });
        sheet.getRow(9).height = 9.95;

        // ── i–v (label B:G | value H:L) ──────────────────────────────────────
        for (let r = 10; r <= 26; r++) sheet.getRow(r).height = 15.75;

        // Reference only merges these value cells across H:I (not H:L) — J:K:L
        // stay blank bordered cells. The CIF/EO value rows (13,14,16,17) merge
        // nothing at all — the number sits in I alone.
        mc('B10:G10', 'i. Advance Authorisation No.', { bold: true });
        mc('H10:I10', billNo);
        mc('B11:G11', 'Date of Issue  ', { bold: false });
        mc('H11:I11', licDate);

        mc('B12:G12', 'ii. CIF Value', { bold: true });
        mc('B13:G13', 'a. In Indian Rupees', { bold: false });
        setCell('H13', 'INR', { size: 11 });
        setCell('I13', num(licData.cif_allowed_inr), { horizontal: 'right', numFmt: ACC_FMT });
        mc('B14:G14', 'b. In free foreign exchange', { bold: false });
        setCell('H14', 'USD', { size: 11 });
        setCell('I14', num(licData.cif_allowed_usd), { horizontal: 'right', numFmt: ACC_FMT });

        mc('B15:G15', 'iii. Export Obligation imposed', { bold: true });
        mc('B16:G16', 'a. In Indian Rupees', { bold: false });
        setCell('H16', 'INR', { size: 11 });
        setCell('I16', num(licData.export_obligation_inr), { horizontal: 'right', numFmt: ACC_FMT });
        mc('B17:G17', 'b. In free foreign exchange', { bold: false });
        setCell('H17', 'USD', { size: 11 });
        setCell('I17', num(licData.export_obligation_usd), { horizontal: 'right', numFmt: ACC_FMT });

        mc('B18:G18', 'iv. Export Obligation Period:', { bold: true });
        merge('H18:I18');
        mc('B19:G19', 'a. Date of expiry of initial export obligation period', { bold: false });
        mc('H19:I19', eopDate);
        mc('B20:G20', 'b. Date of expiry of extended export obligation period', { bold: false });
        setCell('H20', 'NA');
        mc('B21:G21', 'c. Last date of shipment', { bold: false });
        mc('H21:I21', 'As per Export statement ');

        mc('B22:G22', 'v. Norms details:', { bold: true });
        mc('B23:G23', 'a. Serial Number of SION (in case of fixed norms)', { bold: false });
        setCell('H23', 'NA');
        mc('B24:G24', 'b. Meeting number, date, Case No. of the Norms', { bold: false });
        mc('B25:G25', 'Committee vide which norms have been ratified (in', { bold: false });
        mc('B26:G26', 'case of para 4.07 cases)', { bold: false });
        mc('H24:L26', licData.type_of_norm ?? 'No Norms/Self Declaration', { horizontal: 'left' });

        // horizontal rules for every sub-row + label/value divider after col G
        for (let r = 10; r <= 23; r++) {
            edgeBorder(r, r, 2, 12, 'top', 'thin');
            edgeBorder(r, r, 2, 12, 'bottom', 'thin');
            edgeBorder(r, r, 7, 7, 'right', 'thin');
        }
        // Rows 24-26 form one continuous 3-line label ("b. Meeting number... /
        // Committee vide which... / case of para 4.07...") — only the outer top
        // (row 24) and bottom (row 26) edges get a rule; the interior (row 25)
        // has none, or it would cut a line through the middle of the text.
        edgeBorder(24, 24, 2, 7, 'top', 'thin');
        edgeBorder(26, 26, 2, 7, 'bottom', 'thin');
        edgeBorder(24, 26, 7, 7, 'right', 'thin');
        edgeBorder(13, 17, 9, 9, 'left', 'thin');
        gridBorder(24, 26, 8, 12);

        sheet.getRow(27).height = 8;

        // ── 4. Physical / deemed exports ────────────────────────────────────
        [28, 35, 40, 47, 52].forEach(r => { sheet.getRow(r).height = 18; });
        setCell('B28', '4. Details of physical exports / deemed exports made', { bold: true });
        sheet.getRow(29).height = 8;

        sheet.getRow(30).height = 30;
        sheet.getRow(31).height = 30;
        sheet.getRow(32).height = 42;
        mc('B30:B32', 'Sl.No', { horizontal: 'center', wrapText: true });
        mc('C30:C32', 'Products Exported/Supplied (with Technical specification/grade etc. wherever required)', { horizontal: 'center', wrapText: true });
        mc('D30:E30', 'Shipping Bill/Invoice/ ARE3 /CT 3 Details', { horizontal: 'center', wrapText: true });
        mc('F30:I30', 'Quantity Exported / Supplied', { horizontal: 'center', wrapText: true });
        mc('J30:L30', 'FOB Value', { horizontal: 'center', wrapText: true });
        mc('D31:D32', 'No   ', { horizontal: 'center', wrapText: true });
        mc('E31:E32', 'Date  ', { horizontal: 'center', wrapText: true });
        mc('F31:G31', 'Physical exports', { horizontal: 'center', wrapText: true });
        mc('H31:H32', 'Deemed Exports', { horizontal: 'center', wrapText: true });
        mc('I31:I32', 'Total', { horizontal: 'center', wrapText: true });
        mc('J31:J32', 'in case of physical exports (as per S/B) ', { horizontal: 'center', wrapText: true });
        mc('K31:K32', 'In case of deemed exports (as per invoices) ', { horizontal: 'center', wrapText: true });
        mc('L31:L32', 'Actually realized \n(as per e BRC) ', { horizontal: 'center', wrapText: true });
        setCell('F32', 'Direct exports', { horizontal: 'center', wrapText: true });
        setCell('G32', 'Third Party exports', { horizontal: 'center', wrapText: true });
        gridBorder(30, 32, 2, 12);

        sheet.getRow(33).height = 16;
        mc('B33:L33', 'As per Annexure -II enclosed', { horizontal: 'center' });
        gridBorder(33, 33, 2, 12);
        sheet.getRow(34).height = 8;

        // ── 5. Imports made ─────────────────────────────────────────────────
        setCell('B35', '5.Details of imports made against the Authorisation:', { bold: true });
        sheet.getRow(36).height = 38;
        sheet.getRow(37).height = 40;
        mc('B36:B37', 'Sl. \nNo ', { horizontal: 'center', wrapText: true });
        mc('C36:D37', 'Item of \nImport (with Technical specification/grade etc. wherever required)', { horizontal: 'center', wrapText: true });
        mc('E36:H36', 'Imports allowed in the \nAuthorisation ', { horizontal: 'center', wrapText: true });
        mc('I36:J36', 'Bill of Entry Details', { horizontal: 'center', wrapText: true });
        mc('K36:L36', ' Imports effected ', { horizontal: 'center', wrapText: true });
        mc('E37:F37', 'Qty allowed', { horizontal: 'center', wrapText: true });
        mc('G37:H37', 'CIF value (in FFE)', { horizontal: 'center', wrapText: true });
        setCell('I37', 'No.', { horizontal: 'center', wrapText: true });
        setCell('J37', 'Date', { horizontal: 'center', wrapText: true });
        setCell('K37', 'Quantity Imported', { horizontal: 'center', wrapText: true });
        setCell('L37', 'CIF Value utilised (in FFE)', { horizontal: 'center', wrapText: true });
        gridBorder(36, 37, 2, 12);

        sheet.getRow(38).height = 16;
        mc('B38:L38', 'As per Annexure - I enclosed', { horizontal: 'center' });
        gridBorder(38, 38, 2, 12);
        sheet.getRow(39).height = 8;

        // ── 6. Excess imports ──────────────────────────────────────────────
        setCell('B40', '6. Details of excess imports made proportionate to the export obligation fulfilled', { bold: true });
        sheet.getRow(41).height = 8;
        sheet.getRow(42).height = 24;
        sheet.getRow(43).height = 36;
        mc('B42:B43', 'Sl \nNo ', { horizontal: 'center', wrapText: true });
        mc('C42:C43', 'Item of Import ', { horizontal: 'center', wrapText: true });
        mc('D42:D43', 'Quantity', { horizontal: 'center', wrapText: true });
        mc('E42:E43', 'Value', { horizontal: 'center', wrapText: true });
        mc('F42:G43', 'Bill of Entry Details   ', { horizontal: 'center', wrapText: true });
        mc('H42:L42', 'Duty liability and payment Details', { horizontal: 'center', wrapText: true });
        setCell('H43', 'Customs Duty', { horizontal: 'center', wrapText: true });
        setCell('I43', 'Interest', { horizontal: 'center', wrapText: true });
        setCell('J43', 'Total amount paid', { horizontal: 'center', wrapText: true });
        mc('K43:L43', 'Date of payment', { horizontal: 'center', wrapText: true });
        gridBorder(42, 43, 2, 12);

        // Reference keeps one blank bordered data row (full height, not a spacer)
        // under the headers, with a K44:L44 merge for the date-of-payment cell.
        sheet.getRow(44).height = 15.75;
        edgeBorder(44, 44, 3, 10, 'top', 'thin');
        edgeBorder(44, 44, 3, 10, 'bottom', 'thin');
        merge('K44:L44');
        gridBorder(44, 44, 11, 12);
        sheet.getRow(45).height = 16;
        mc('B45:L45', 'NOT APPLICABLE', { bold: true, underline: true, horizontal: 'center' });
        gridBorder(45, 45, 2, 12);
        sheet.getRow(46).height = 8;

        // ── 7. Composition fee ─────────────────────────────────────────────
        setCell('B47', '7.Details of payment of payment composition fee :', { bold: true });
        sheet.getRow(48).height = 8;
        sheet.getRow(49).height = 64;
        setCell('B49', 'Sl No', { horizontal: 'center', wrapText: true });
        mc('C49:D49', 'Shortfall in FOB (in US$)', { horizontal: 'center', wrapText: true });
        mc('E49:F49', 'Shortfall in equivalent Rs.', { horizontal: 'center', wrapText: true });
        mc('G49:H49', 'Composition Fee required to be paid in terms of Para 4.49 of HBP', { horizontal: 'center', wrapText: true });
        mc('I49:J49', 'Amount paid', { horizontal: 'center', wrapText: true });
        mc('K49:L49', 'Date of payment & mode of payment', { horizontal: 'center', wrapText: true });
        gridBorder(49, 49, 2, 12);

        sheet.getRow(50).height = 16;
        mc('B50:L50', 'NOT APPLICABLE', { bold: true, underline: true, horizontal: 'center' });
        gridBorder(50, 50, 2, 12);
        sheet.getRow(51).height = 8;

        // ── 8. Unutilised restricted items ─────────────────────────────────
        setCell('B52', '8. Details of unutilised items which are restricted to be imported at the time of import :', { bold: true });
        sheet.getRow(53).height = 8;
        sheet.getRow(54).height = 54;
        setCell('B54', 'Sl No', { horizontal: 'center', wrapText: true });
        mc('C54:D54', 'Item of import', { horizontal: 'center', wrapText: true });
        mc('E54:F54', 'Unutilised quantity', { horizontal: 'center', wrapText: true });
        mc('G54:H54', 'CIF value', { horizontal: 'center', wrapText: true });
        mc('I54:J54', 'Amount of composition fee payable', { horizontal: 'center', wrapText: true });
        mc('K54:L54', 'Amount of composition fee paid (date and mode of payment)', { horizontal: 'center', wrapText: true });
        gridBorder(54, 54, 2, 12);

        sheet.getRow(55).height = 16;
        mc('B55:L55', 'NOT APPLICABLE', { bold: true, underline: true, horizontal: 'center' });
        gridBorder(55, 55, 2, 12);
        edgeBorder(55, 55, 2, 12, 'bottom', 'medium');
        sheet.getRow(56).height = 8;

        // ── Section-label top rule ───────────────────────────────────────────
        // Reference only rules the top of row 28 (the block directly after the
        // outer-bordered rows 10-26) — rows 35/40/47/52 sit right under their own
        // preceding table's bottom rule already, so no extra line here.
        edgeBorder(28, 28, 2, 12, 'top', 'thin');

        // ── Declaration / undertaking ─────────────────────────────────────
        sheet.getRow(57).height = 18;
        // Reference places this label at G57 (not merged/centered across the row).
        setCell('G57', 'DECLARATION / UNDERTAKING', { bold: true });
        edgeBorder(57, 57, 2, 12, 'top', 'medium');
        sheet.getRow(58).height = 8;
        sheet.getRow(59).height = 16;
        setCell('B59', 'I / We hereby declare that: ', { bold: false });
        sheet.getRow(60).height = 8;

        const declarations = [
            ['the particulars and the statements made in this application are true and correct to the best of my/our knowledge and belief and nothing has been concealed or held there from and if found incorrect or false will render me/us liable for any penal action or other consequences as may be prescribed in law or otherwise warranted. ', 50],
            ['we would abide by the provisions of the Foreign Trade (Development and Regulation) Act, 1992, the Rules and Orders framed there under, the Foreign trade Policy, the Handbook Procedure and the ITC(HS) Classification of Export & Import Items, as amended from time to time. ', 47],
            ['None of the Proprietor/ Partner(s) / Director(s) / Karta / Trustee of the firm / company, as the case may be, is / are a Proprietor / Partner(s) / Director(s) / Karta / Trustee in any other firm / Company which has come to the adverse notice of DGFT or in the caution list of RBI.', 47],
            ['I/We have perused the list of SCOMET items as contained in the Appendix 3 to the Schedule 2 of the ITC(HS) Classifications of Export-Import Items, and that the item(s) exported / proposed to be exported does not fall within this list and that I/We agree to abide by the provisions of the Policy for export of SCOMET items contained in the Foreign Trade Policy, Schedule 2 of ITC(HS) and the HBP, irrespective of the scheme under which the item is exported / proposed to be exported (the underlined portion will be deleted in case an application for export license for SCOMET item is being filed).', 80],
            ['No export proceeds are outstanding beyond the prescribed period as laid down by RBI or such extended  period for which RBI permission has been obtained.  ', 32],
            ['Freight, Insurance and Commission values as mentioned in the application are based on actual transaction  values. ', 16],
            ['For the purpose of calculating entitlement, commission amount has been excluded. ', 16],
            ['that I/We have updated the IEC profiles in ANF 1', 16],
            ['We have not been penalized under any of the following Acts (as amended from time to time): (i) The Customs Act, 1962, (ii) The Central Excise Act 1944, (iii) Foreign Trade (Development & Regulation) Act 1992, and (iv) The Foreign Exchange Management Act,1999; (v) The Conservation of Foreign Exchange, Prevention of Smuggling Activities Act, 1974 B.  ', 60],
            ['I am authorised to verify and sign this declaration as per Paragraph 9.06 of the FTP.  ', 16],
            ['The application contains (As per Export Statement )of EDI Shipping Bills and Nil Nos of Non-EDI Shipping Bills', 16],
        ];

        // Reference mixes vertical alignment across these paragraphs: most are
        // vertically centered, but items (v), (viii), (x), (xi) — 0-based indices
        // 4, 7, 9, 10 — are top-aligned instead.
        const topAlignedDeclarations = new Set([4, 7, 9, 10]);
        let dRow = 61;
        declarations.forEach(([text, minH], i) => {
            setCell(`B${dRow}`, i + 1, { horizontal: 'center' });
            // ExcelJS's vertical alignment enum is 'middle', not 'center' — passing
            // 'center' here was silently dropped, leaving every paragraph unaligned.
            const vertical = topAlignedDeclarations.has(i) ? 'top' : 'middle';
            if (i === 10) {
                // Reference bolds + underlines "As per Export Statement " within
                // this paragraph — a partial run, not the whole cell, so it needs
                // rich text rather than a single uniform font.
                merge(`C${dRow}:L${dRow}`);
                const cell = sheet.getCell(`C${dRow}`);
                cell.value = {
                    richText: [
                        { text: 'The application contains (', font: { name: 'Calibri', size: FS } },
                        { text: 'As per Export Statement ', font: { name: 'Calibri', size: FS, bold: true, underline: true } },
                        { text: ')of EDI Shipping Bills and Nil Nos of Non-EDI Shipping Bills', font: { name: 'Calibri', size: FS } },
                    ],
                };
                cell.alignment = { horizontal: 'left', vertical, wrapText: true };
            } else {
                mc(`C${dRow}:L${dRow}`, text, { wrapText: true, vertical });
            }
            sheet.getRow(dRow).height = Math.max(minH, getLines(text, 92) * 14);
            sheet.getRow(dRow + 1).height = 8;
            dRow += 2;
        });

        // ── Signature block (rows 83-89) ──────────────────────────────────
        const sigStart = dRow;              // 83
        setCell(`C${sigStart}`, 'Place:- Pune', { bold: true });
        setCell(`H${sigStart}`, 'Signature of the Applicant', { bold: true });
        setCell(`C${sigStart + 1}`, `Date: ${signDate}`, { bold: false });
        ['Name', 'Designation', 'Official Address', 'Telephone', 'Residential Add', 'Email Add']
            .forEach((label, i) => setCell(`H${sigStart + 1 + i}`, label, { bold: true }));
        for (let r = sigStart; r <= sigStart + 6; r++) sheet.getRow(r).height = 15.75;
        const lastRow = sigStart + 6;

        // ── Outer medium rule around the whole form ───────────────────────
        // Reference actually draws two separate boxes — the form (rows 2-55) and
        // the declaration/signature block (rows 57-89) — with a real gap (no
        // border at all) at row 56, not one continuous frame.
        applyOuterBorder(sheet, 2, 55, 2, 12, 'medium');
        applyOuterBorder(sheet, 57, lastRow, 2, 12, 'medium');

        // Not calling applyCalibri12 here: it forces every cell to 12pt (wiping
        // out the handful of reference cells that are intentionally 11pt, e.g.
        // H13/H14/H16/H17) and trims every string (stripping trailing spaces the
        // reference's own label text actually has, which shifts wrapped-paragraph
        // line breaks). Cells already styled via setCell/mc keep their own
        // explicit font; only blank/untouched decorative cells (border-only,
        // inside merged ranges, never passed through setCell/mc) need a default
        // Calibri 12 so they don't fall back to ExcelJS's own default font.
        sheet.eachRow({ includeEmpty: true }, (row) => {
            row.eachCell({ includeEmpty: true }, (cell) => {
                if (!cell.font || !cell.font.name) {
                    cell.font = { name: 'Calibri', size: 12 };
                }
            });
        });
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(
            new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
            `ANF_4F_${billNo}_${getTodaysDate()}.xlsx`
        );
    };

    const exportApp4HRegisterExcel = async () => {
        if (!billNo) {
            message.error("Please select an Auth Number to download Appendix 4H.");
            return;
        }

        const licData = masterSummaryData.find(
            item => String(item.lic_no || '').trim() === String(billNo || '').trim()
        ) || masterSummaryData[0] || {};
        const licDate = licData.lic_date ? dayjs(licData.lic_date).format('DD.MM.YYYY') : '';
        const registerDate = dayjs().format('DD.MM.YYYY');

        let workbook = new ExcelJS.Workbook();
        let sheet = workbook.addWorksheet('APPENDIX - 4H ');

        sheet.pageSetup = {
            paperSize: 9,
            orientation: 'landscape',
            scale: 65,
            horizontalCentered: true,
            margins: { left: 0.2, right: 0.21, top: 0.28, bottom: 0.52, header: 0, footer: 0 }
        };
        // Reference shows gridlines (no showGridLines override).

        const colWidths = [
            3.71,  // A
            6.71,  // B
            13.86, // C
            39.14, // D
            9.0,   // E
            8.57,  // F
            8.57,  // G
            16.57, // H
            8.71,  // I
            40.29, // J
            12.57, // K
            13.0,  // L
            10.43, // M
            10.57, // N
            9.71,  // O
            10.29, // P
        ];
        sheet.columns = colWidths.map(w => ({ width: w }));

        const thin = { style: 'thin' };
        const allBorders = { top: thin, left: thin, bottom: thin, right: thin };

        // Helper to extract short UOM (e.g. "NUMBERS (NOS)" -> "NOS")
        const cleanUom = (u) => {
            if (!u) return '';
            const m = String(u).match(/\(([^)]+)\)/);
            return m ? m[1].trim() : String(u).trim();
        };

        // Sum actual Export St quantities once per export serial for this licence.
        // SHB accountability repeats export quantities for each imported input.
        const exportQtyBySr = new Map();
        exportStData.forEach(item => {
            if (String(item.auth_number || '').trim() !== String(billNo || '').trim()) return;
            const key = String(item.export_sr_no ?? '').trim();
            if (!key) return;
            exportQtyBySr.set(key, (exportQtyBySr.get(key) || 0) + (Number(item.export_qty) || 0));
        });

        const filteredShbRows = shbWiseData.filter(
            item => String(item.auth_number || '').trim() === String(billNo || '').trim()
        );
        const inputSummaryBySr = new Map();
        filteredShbRows.forEach(item => {
            const key = String(item.import_item_sr_no ?? '').trim();
            if (!key) return;

            if (!inputSummaryBySr.has(key)) {
                inputSummaryBySr.set(key, {
                    importSrNo: item.import_item_sr_no,
                    inputDescription: item.import_item_description || '',
                    qtyAllowed: Number(item.imp_qty_as_per_lic) || 0,
                    actualImportQty: 0,
                    inputUom: item.imp_uom || item.actual_import_uom || '',
                    expSrNo: item.exp_sr_no,
                    product: item.exp_description || '',
                    exportQty: exportQtyBySr.get(String(item.exp_sr_no ?? '').trim()) ?? 0,
                    exportUom: item.exp_uom || '',
                    qtyConsumed: 0,
                });
            }

            const summary = inputSummaryBySr.get(key);
            summary.actualImportQty = Math.max(summary.actualImportQty, Number(item.actual_imports_made) || 0);
            summary.qtyConsumed += Number(item.qty_used) || 0;
        });

        const registerRows = Array.from(inputSummaryBySr.values())
            .sort((a, b) => sortBySrNo(a, b, 'importSrNo'));

        const mergeAndSet = (range, value, bold = false, horizontal = 'center', wrapText = true, vertical = 'middle') => {
            if (range.includes(':')) {
                const parts = range.split(':');
                if (parts[0] !== parts[1]) {
                    sheet.mergeCells(range);
                }
            }
            const startCell = range.split(':')[0];
            const cell = sheet.getCell(startCell);
            cell.value = value;
            cell.font = { bold };
            cell.alignment = { horizontal, vertical, wrapText };
            return cell;
        };

        const applyBorders = (startRow, endRow, startCol, endCol) => {
            for (let r = startRow; r <= endRow; r++) {
                for (let c = startCol; c <= endCol; c++) {
                    sheet.getCell(r, c).border = allBorders;
                }
            }
        };

        // Row 1 is a genuine blank gap in the reference — no border, no value.

        // Row 2 — title (bold)
        sheet.getRow(2).height = 15.75;
        mergeAndSet('B2:P2', 'APPENDIX - 4H', true, 'center');
        applyBorders(2, 2, 2, 16);

        // Row 3 — subtitle (NOT bold in the reference)
        sheet.getRow(3).height = 15.75;
        mergeAndSet('B3:P3',
            'REGISTER FOR ACCOUNTING THE CONSUMPTION AND STOCKS OF DUTY FREE IMPORTED OR DOMESTICALLY PROCURED RAW MATERIALS, COMPONENTS ETC. ALLOWED UNDER ADVANCE AUTHORISATION / DFIA*  ',
            false, 'left', true, 'center');
        applyBorders(3, 3, 2, 16);

        // Row 4 — true blank gap, no border at all (reference leaves it fully unstyled).
        sheet.getRow(4).height = 15.75;

        // Row 5 — top-level group headers (not bold).
        sheet.getRow(5).height = 48.75;
        mergeAndSet('B5:F5', 'INPUTS ALLOWED IN THE AUTHORISATION(S)*** (ANY NO. OF AUTHORISATIONS CAN BE CLUBBED TOGETHER FOR THE PURPOSE OF ACCOUNTING OF INPUTS)', false, 'center', true, 'middle');
        mergeAndSet('G5:K5', 'PRODUCT (S) EXPORTED UNDER THE \nAUTHORISATION (S) ', false, 'center', true, 'top');
        mergeAndSet('L5:L7', 'EXCESS INPUTS, \nIF ANY  ALLOWED UNDER  THE  AUTHO\nRISATIO N(S). \n(4 - 8) ', false, 'center', true, 'top');
        mergeAndSet('M5:O5', 'IN CASE OF EXCESS AS IN COLUMN 9', false, 'center', true, 'top');
        mergeAndSet('P5:P7', 'REMARKS \n', false, 'center', true, 'middle');

        // Row 6 — sub-headers (most span down into row 7; J/K stay in row 6 only).
        sheet.getRow(6).height = 15.75;
        mergeAndSet('B6:B7', 'Sl \nNo\n', false, 'center', true, 'top');
        mergeAndSet('C6:C7', 'Authorization No(s) with date (A No. of authoristions can be clubbed together for accounting purpose)', false, 'center', true, 'top');
        mergeAndSet('D6:D7', 'Inputs ', false, 'center', true, 'middle');
        mergeAndSet('E6:E7', 'Quantity ', false, 'center', true, 'middle');
        mergeAndSet('F6:F7', 'UOM', false, 'center', true, 'middle');
        mergeAndSet('G6:G7', 'Export\nSr No', false, 'center', true, 'middle');
        mergeAndSet('H6:H7', 'Product ', false, 'center', true, 'middle');
        mergeAndSet('I6:I7', 'Quantity in\nNos', false, 'center', true, 'middle');
        mergeAndSet('J6:K6', 'Inputs Actually \nconsumed for \nthe exported product', false, 'center', true, 'top');
        mergeAndSet('M6:M7', 'Additional exports effected in proportion to excess inputs', false, 'center', true, 'top');
        mergeAndSet('N6:N7', 'Input quantity reduced proportionately in \nthe authorisation', false, 'center', true, 'top');
        mergeAndSet('O6:O7', 'Customs dutypaid along\nwith interest\n', false, 'center', true, 'top');

        // Row 7 — only J7/K7 have their own content; everything else already
        // spans down from row 6.
        sheet.getRow(7).height = 114.0;
        mergeAndSet('J7:J7', 'Inputs ', false, 'center', false, 'middle');
        mergeAndSet('K7:K7', 'Quantity \n(Including actual \nwastage \nincurred)\n', false, 'center', true, 'middle');

        applyBorders(5, 7, 2, 16);

        // Row 8 — decorative column-number row (NOT bold in the reference)
        sheet.getRow(8).height = 15.75;
        [1, 2, 3, 4, '4A', 5, '5A', 6, 7, 8, 9, 10, 11, 12, 13].forEach((val, i) => {
            const cell = sheet.getCell(8, i + 2);
            cell.value = val;
            cell.font = { bold: false };
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
            cell.border = allBorders;
        });

        let dataRow = 9;
        const dataStartRow = dataRow;
        registerRows.forEach((item, idx) => {
            const row = sheet.getRow(dataRow);
            // Reference preserves the input description's original casing (not uppercased).
            const descD = (item.inputDescription || '').replace(/#/g, '');
            const descJ = descD;
            // Format product description with proper spacing around ITEM NO. / PROJECT NO. :
            const descH = (item.product || '').replace(/ITEM NO\.\s*\/\s*PROJECT NO\.\s*:?/i, 'ITEM NO. / PROJECT NO. : ');

            const linesD = getLines(descD, 40);
            const linesJ = getLines(descJ, 40);
            const linesH = getLines(descH, 36);

            const maxLines = Math.max(linesD, linesJ, linesH);
            row.height = Math.max(24, maxLines * 16);

            row.values = [
                '',
                item.importSrNo,                           // (1) Sl No = input serial number
                '',                                        // (2) Authorisation No — merged cell set below
                descD,                                     // (3) Inputs
                item.actualImportQty,                      // (4) Quantity — Quantity actually imported (0 where not imported)
                cleanUom(item.inputUom),                   // (4A) UOM (NOS)
                item.expSrNo,                              // Export Sr No
                descH,                                     // (5) Product
                item.exportQty,                            // (6) Quantity in Nos
                descJ,                                     // (7) Inputs actually consumed — Inputs
                item.qtyConsumed,                          // (8) Quantity consumed
                { formula: `+E${dataRow}-K${dataRow}` },   // (9) Excess Inputs (4 - 8)
                'NA',
                'NA',
                'NA',
                'NA',
            ];

            row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
                if (colNumber >= 2 && colNumber <= 16) {
                    cell.border = allBorders;
                    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
                    cell.font = { name: 'Calibri', size: 10 };
                }
            });

            sheet.getCell(dataRow, 4).alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
            sheet.getCell(dataRow, 10).alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };

            const numCols = [5, 9, 11, 12];
            numCols.forEach(c => {
                const cell = sheet.getCell(dataRow, c);
                cell.numFmt = '#,##0.00;(#,##0.00);"-"';
            });

            dataRow++;
        });

        const totalRows = registerRows.length;
        const lastDataRow = dataStartRow + totalRows - 1;
        if (totalRows > 0) {
            if (totalRows > 21) {
                // Split merge matching reference page split
                sheet.mergeCells(`C${dataStartRow}:C${dataStartRow + 20}`);
                const cellC1 = sheet.getCell(dataStartRow, 3);
                cellC1.value = `${billNo} Date. ${licDate}`;
                cellC1.font = { name: 'Calibri', size: 10 };
                cellC1.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
                applyBorders(dataStartRow, dataStartRow + 20, 3, 3);

                sheet.mergeCells(`C${dataStartRow + 21}:C${lastDataRow}`);
                const cellC2 = sheet.getCell(dataStartRow + 21, 3);
                cellC2.value = `${billNo} Date. ${licDate}`;
                cellC2.font = { name: 'Calibri', size: 10 };
                cellC2.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
                applyBorders(dataStartRow + 21, lastDataRow, 3, 3);
            } else if (lastDataRow > dataStartRow) {
                sheet.mergeCells(`C${dataStartRow}:C${lastDataRow}`);
                const cellC = sheet.getCell(dataStartRow, 3);
                cellC.value = `${billNo} Date. ${licDate}`;
                cellC.font = { name: 'Calibri', size: 10 };
                cellC.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
                applyBorders(dataStartRow, lastDataRow, 3, 3);
            }

            // Reference visually "merges" the export-side columns (G/H/I) across
            // consecutive rows that share the same export item, by suppressing
            // the bottom border on every row but the last in that run — without
            // an actual Excel merge.
            for (let r = dataStartRow; r <= lastDataRow; r++) {
                const nextRow = r + 1;
                const sameAsNext = nextRow <= lastDataRow &&
                    String(registerRows[r - dataStartRow].expSrNo) === String(registerRows[nextRow - dataStartRow].expSrNo);
                if (sameAsNext) {
                    [7, 8, 9].forEach(c => {
                        const cell = sheet.getCell(r, c);
                        cell.border = { ...cell.border, bottom: undefined };
                    });
                }
            }
        }

        // ── Transition row above the footnote (top rule only, no value) ───────
        const preNoteRow = lastDataRow + 1;
        sheet.getRow(preNoteRow).height = 15.75;
        for (let c = 2; c <= 16; c++) {
            sheet.getCell(preNoteRow, c).border = { top: thin };
        }

        // ── Footnote — one merged cell, all three notes combined into a single
        // paragraph, exactly as the reference stores it (including its own
        // double space and curly quotes).
        const noteRow = preNoteRow + 1;
        const noteText = '*In case of Post export replenishment, details of inputs used (whether duty paid or not) in the exported  product has to be furnished.\n**Applicable only in case either partial import or “NIL” import has been effected.\n*** In case of transferable DFIA, information in the above format has to be furnished individual DFIA wise.We declare that the aforesaid particulars are correct.';
        sheet.getRow(noteRow).height = 57.75;
        sheet.mergeCells(`B${noteRow}:P${noteRow}`);
        const noteCell = sheet.getCell(noteRow, 2);
        noteCell.value = noteText;
        noteCell.font = { name: 'Calibri', size: 10 };
        noteCell.alignment = { horizontal: 'left', vertical: 'top', wrapText: true };
        applyBorders(noteRow, noteRow, 2, 16);

        // ── Transition row below the footnote (top rule only, no value) ───────
        const postNoteRow = noteRow + 1;
        sheet.getRow(postNoteRow).height = 15.75;
        for (let c = 2; c <= 16; c++) {
            sheet.getCell(postNoteRow, c).border = { top: thin };
        }

        // ── Footer / signature block ───────────────────────────────────────────
        // Place & "Official Seal/stamp" share one row; Date & the signature
        // label share the next; the remaining fields follow one per row.
        const placeRow = postNoteRow + 1;
        const dateRow = placeRow + 1;
        const footerEndRow = dateRow + 6; // 5 signature fields + the closing border row

        for (let r = placeRow; r <= footerEndRow; r++) {
            sheet.getRow(r).height = 15.75;
            sheet.getCell(r, 2).border = { left: thin };
            sheet.getCell(r, 16).border = { right: thin };
        }

        sheet.getCell(placeRow, 3).value = 'Place :- Pune';
        sheet.getCell(placeRow, 3).font = { name: 'Calibri', size: 10 };
        sheet.getCell(placeRow, 3).alignment = { horizontal: 'left', vertical: 'middle' };
        sheet.getCell(placeRow, 10).value = 'Official Seal/stamp';
        sheet.getCell(placeRow, 10).font = { name: 'Calibri', size: 10 };
        sheet.getCell(placeRow, 10).alignment = { horizontal: 'left', vertical: 'middle' };

        sheet.getCell(dateRow, 3).value = `Date:- ${registerDate}`;
        sheet.getCell(dateRow, 3).font = { name: 'Calibri', size: 10 };
        sheet.getCell(dateRow, 3).alignment = { horizontal: 'left', vertical: 'middle' };
        sheet.getCell(dateRow, 10).value = 'Signature of the Authorisation Holder:-';
        sheet.getCell(dateRow, 10).font = { name: 'Calibri', size: 10 };
        sheet.getCell(dateRow, 10).alignment = { horizontal: 'left', vertical: 'middle' };

        const sigLabels = [
            'Name in block letters:-',
            'Designation:-',
            'Full official address:-',
            'Full Residential add:-',
            'Telephone:-',
        ];
        sigLabels.forEach((label, i) => {
            const r = dateRow + 1 + i;
            const cell = sheet.getCell(r, 10);
            cell.value = label;
            cell.font = { name: 'Calibri', size: 10 };
            cell.alignment = { horizontal: 'left', vertical: 'middle' };
        });

        // ── Closing row — bottom edge of the outer box ─────────────────────────
        for (let c = 2; c <= 16; c++) {
            sheet.getCell(footerEndRow, c).border = { ...sheet.getCell(footerEndRow, c).border, bottom: thin };
        }

        // Reference's auto-filter covers only the number row + data rows.
        sheet.autoFilter = `B8:P${lastDataRow}`;

        applyOuterBorder(sheet, 2, footerEndRow, 2, 16, 'thin');
        // Row 4 is a genuine gap in the reference's outer box — clear the left/
        // right edge the outer-border pass just added across the full height.
        sheet.getCell(4, 2).border = {};
        sheet.getCell(4, 16).border = {};
        // Reference has no explicit print area set.

        applyCalibri12(workbook);
        const buffer = await workbook.xlsx.writeBuffer();
        // Register the AutoFilter via the hidden _xlnm._FilterDatabase defined
        // name so the dropdown buttons actually render (see addFilterDatabaseName).
        const patchedBlob = await addFilterDatabaseName(buffer, 'APPENDIX - 4H ', `$B$8:$P$${lastDataRow}`);
        saveAs(patchedBlob, `Appendix_4H_Register_${billNo}_${getTodaysDate()}.xlsx`);
    };

    const exportApp4HExcel = async () => {
        if (!billNo) return;
        let workbook = new ExcelJS.Workbook();
        let sheet = workbook.addWorksheet('Appendix 4H');

        sheet.pageSetup = {
            paperSize: 9,
            orientation: 'landscape',
            scale: 80,
            horizontalCentered: true,
            margins: { left: 0.25, right: 0.27, top: 0.59, bottom: 0.75, header: 0, footer: 0 }
        };
        // Reference shows gridlines (not hidden).
        sheet.columns = [
            { width: 6.86 },   // A
            { width: 8.14 },   // B
            { width: 13.86 },  // C
            { width: 18.43 },  // D
            { width: 13.71 },  // E
            { width: 16.57 },  // F
            { width: 15.0 },   // G
            { width: 18.86 },  // H
            { width: 12.57 },  // I
            { width: 10.0 },   // J
            { width: 10.43 },  // K
            { width: 10.57 },  // L
            { width: 9.71 },   // M
            { width: 17.29 },  // N
        ];

        const thin = { style: 'thin' };
        const allBorders = { top: thin, left: thin, bottom: thin, right: thin };
        const baseFont = { name: 'Calibri', size: 11 };

        const licData = masterSummaryData.find(
            item => String(item.lic_no || '').trim() === String(billNo || '').trim()
        ) || masterSummaryData[0] || {};
        const selectedPAN = licData?.pan_number || '';

        const licPeriod = (() => {
            if (licData.fy && String(licData.fy).trim()) return String(licData.fy).trim();
            const d = licData.lic_date ? dayjs(licData.lic_date) : null;
            if (!d || !d.isValid()) return '';
            const y = d.year(), m = d.month() + 1;
            const start = m >= 4 ? y : y - 1;
            return `${String(start).slice(2)}-${String(start + 1).slice(2)}`;
        })();

        const para1 = `I/We hereby confirm that I / We have examined the prescribed registers and also the relevant records of M/s. ${selectedIECName} having IEC number ${selectedIEC} and PAN number ${selectedPAN} for the licensing period (s) ${licPeriod} and hereby certify that the information furnished above is true and correct in all respects; no part of it is false or misleading and no relevant information has been concealed or withheld`;

        const para2and3 = `I / We fully understand that any statement made in this certificate, if proved incorrect or false, will render me/us liable to face any penal action or other consequences as may be prescribed in law or otherwise warranted.\n\nI/We further declare that neither I, nor any of my/ our partners is a partner, director, or an employee of the above-named entity, its Group companies or its associated concerns.`;

        // Reference only merges the 4 content rows (B1:N1, B3:N3, B5:N5, B7:N7) —
        // the spacer rows (2, 4, 6, 8, 9, 15) are left as individual, unmerged cells.
        // Every row height below is the reference's own exact stored value.
        [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15].forEach(r => {
            sheet.getRow(r).height = (r === 5) ? 59.25 : (r === 7) ? 71.25 : 15.75;
        });

        // Row 1 – document title in B1:N1
        sheet.mergeCells('B1:N1');
        const titleCell = sheet.getCell('B1');
        titleCell.value = 'Appendix 4H';
        titleCell.font = { ...baseFont, bold: true };
        titleCell.alignment = { horizontal: 'center', vertical: 'middle' };

        // Row 3 – certificate heading in B3:N3 (not bold in the reference)
        sheet.mergeCells('B3:N3');
        const headingCell = sheet.getCell('B3');
        headingCell.value = 'CHARTERED ACCOUNTANT / COST ACCOUNTANT/CERTIFICATE';
        headingCell.font = { ...baseFont, bold: false };
        headingCell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };

        // Row 5 – declaration paragraph 1 in B5:N5
        sheet.mergeCells('B5:N5');
        const bodyCell1 = sheet.getCell('B5');
        bodyCell1.value = para1;
        bodyCell1.font = baseFont;
        bodyCell1.alignment = { horizontal: 'left', vertical: 'top', wrapText: true };

        // Row 7 – declaration paragraphs 2 & 3 in B7:N7
        sheet.mergeCells('B7:N7');
        const bodyCell2 = sheet.getCell('B7');
        bodyCell2.value = para2and3;
        bodyCell2.font = baseFont;
        bodyCell2.alignment = { horizontal: 'left', vertical: 'top', wrapText: true };

        // Rows 10-14 – Place/Date at B10/B11 (not bold), Signature block at G10-G14 (not bold)
        const formattedDateDots = dayjs().format('DD.MM.YYYY');
        sheet.getCell('B10').value = 'Place: Pune';
        sheet.getCell('B10').font = { ...baseFont, bold: false };
        sheet.getCell('B10').alignment = { horizontal: 'left', vertical: 'middle' };

        sheet.getCell('B11').value = `Date: ${formattedDateDots}`;
        sheet.getCell('B11').font = { ...baseFont, bold: false };
        sheet.getCell('B11').alignment = { horizontal: 'left', vertical: 'middle' };

        const signatoryLines = [
            '(Signature and Stamp/ Seal of the Signatory)',
            '(Chartered Accountant/ Cost Accountant)',
            'Name of the Signatory:',
            'Address:',
            'Membership No:',
        ];
        signatoryLines.forEach((text, i) => {
            const cell = sheet.getCell(10 + i, 7); // Col G
            cell.value = text;
            cell.font = { ...baseFont, bold: false };
            cell.alignment = { horizontal: 'left', vertical: 'middle' };
        });

        // Horizontal rules: row 3 is boxed top+bottom on its own; rows 1/5 get no
        // extra rule beyond the outer box; row 7 gets a closing bottom rule only
        // (it closes off both paragraph rows together, not row 5 individually).
        for (let c = 2; c <= 14; c++) {
            sheet.getCell(3, c).border = { ...allBorders };
            sheet.getCell(7, c).border = { ...sheet.getCell(7, c).border, bottom: thin };
        }
        applyOuterBorder(sheet, 1, 15, 2, 14, 'thin');

        sheet.pageSetup.printArea = 'B1:N15';

        applyCalibri12(workbook);
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `Appendix_4H_${billNo}_${getTodaysDate()}.xlsx`);
    };

    // Normalise a licence address for inline use in the certificates. The raw
    // adv_auth_details.address often carries embedded line breaks and doubled commas
    // (e.g. Rivaan "…TAL -\nHAVELI,,PUNE,411041"), which break the certificate's
    // expected single-line "M/s. <name> Add. <address>" format. Collapse to one clean
    // comma-separated line.
    const cleanAddress = (addr) => String(addr || '')
        .replace(/[\r\n]+/g, ' ')
        .replace(/[ \t]{2,}/g, ' ')
        .replace(/\s*,\s*/g, ', ')
        .replace(/(?:,\s*){2,}/g, ', ')
        .replace(/^[\s,]+|[\s,]+$/g, '')
        .trim();

    const exportDisclaimerExcel = async () => {
        if (!billNo) return;
        const licData = masterSummaryData.find(
            item => String(item.lic_no || '').trim() === String(billNo || '').trim()
        );
        const companyAddress = cleanAddress(licData?.registered_address) || cleanAddress(licData?.address);
        if (!companyAddress) {
            message.error(`No licensee address is available for licence ${billNo}.`);
            return;
        }
        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet('Disclaimer ');

        sheet.pageSetup = {
            paperSize: 9,
            orientation: 'portrait',
            margins: { left: 0.78, right: 0.32, top: 0.6, bottom: 0.75, header: 0, footer: 0 },
        };
        sheet.columns = [
            { width: 9.14 }, { width: 16.57 }, { width: 10.71 }, { width: 9.14 }, { width: 9.14 },
            { width: 9.14 }, { width: 9.14 }, { width: 9.14 }, { width: 10.71 },
        ];
        sheet.properties.defaultRowHeight = 17.25;

        const FS = 13;
        const WRAP_W = 72;
        // Issuing CA firm — a fixed professional, same pattern as the CE certificate's
        // hardcoded Chartered Engineer.
        const CA_NAME = 'Mihir Nimbolkar';
        const CA_FIRM = 'Nimbolkar & Associates';
        const cleanName = stripTradeSuffix(licData.company_name || selectedIECName);
        const licDateDotted = licData.lic_date ? dayjs(licData.lic_date).format('DD.MM.YYYY') : '';
        const signDate = dayjs().format('DD.MM.YYYY');

        const at = (ref, text, opts = {}) => {
            const { bold = false, horizontal = 'left', vertical = 'top', wrapText = false, merge = null, minHeight = 0 } = opts;
            if (merge) { try { sheet.mergeCells(merge); } catch (e) { /* already merged */ } }
            const cell = sheet.getCell(ref);
            cell.value = text;
            cell.font = { name: 'Calibri', size: FS, bold };
            cell.alignment = { horizontal, vertical, wrapText };
            const rowNum = parseInt(ref.replace(/[A-Z]/g, ''), 10);
            const h = Math.max(minHeight, wrapText ? getLines(text, WRAP_W) * 16 : 0);
            if (h) sheet.getRow(rowNum).height = h;
        };

        at('B9', `Date: ${signDate}`);
        at('B11', 'To,', { vertical: 'middle' });
        at('B12', cleanName, { merge: 'B12:I12', wrapText: true });
        at('B13', companyAddress, { merge: 'B13:I13', wrapText: true });

        at('C17', 'INTEGRATED TAX / CENVAT NON AVAILEMENT CERTIFICATE ', { bold: true });

        at('B20', `I ${CA_NAME}, Proprietor of ${CA_FIRM} herewith certify that M/S`, { merge: 'B20:I20', minHeight: 18 });
        at('B22', `${cleanName} having address, /${companyAddress}`, { merge: 'B22:I22', wrapText: true, minHeight: 34.5 });
        at('B24', `IEC No.${selectedIEC} has not availed any INTEGRATED TAX / CENVAT credit against advance License No.:${billNo} date ${licDateDotted}`, { merge: 'B24:I24', wrapText: true, minHeight: 35.25 });

        at('B27', 'Thanks & Regards,');
        at('B29', `For ${CA_FIRM}`, { bold: true });
        at('B33', 'Proprietor', { bold: true });

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `Disclaimer_Cert_${billNo}_${getTodaysDate()}.xlsx`);
    };

    const exportCEExcel = async () => {
        if (!billNo) return;
        let workbook = new ExcelJS.Workbook();
        const licData = masterSummaryData.find(
            item => String(item.lic_no || '').trim() === String(billNo || '').trim()
        ) || masterSummaryData[0] || {};

        const rawAddr = cleanAddress(licData?.registered_address || licData?.address || '');
        const companyAddress = rawAddr.replace(/\s+/g, ' ').trim();
        const licDateDotted = licData.lic_date ? dayjs(licData.lic_date).format('DD.MM.YYYY') : '';
        const signDate = dayjs().format('DD.MM.YYYY');

        let sheetCE = workbook.addWorksheet('CE');
        sheetCE.pageSetup = {
            paperSize: 9,
            orientation: 'portrait',
            printArea: "'CE'!$B$1:$B$27",
            margins: { left: 0.4, right: 0.44, top: 0.39, bottom: 0.75, header: 0.3, footer: 0.3 },
        };
        sheetCE.columns = [
            { width: 9.140625 }, { width: 98.140625 }, { width: 9.140625 }, { width: 12.42578125 }, { width: 9.140625 },
        ];
        sheetCE.properties.defaultRowHeight = 17.25;

        const FS1 = 13;
        const cleanName = stripTradeSuffix(selectedIECName);

        const para1 = (rowNum, text, opts = {}) => {
            const { bold = false, horizontal = 'left', vertical = 'middle', height = null } = opts;
            const cell = sheetCE.getCell(`B${rowNum}`);
            cell.value = text;
            cell.font = { name: 'Calibri', size: FS1, bold };
            cell.alignment = { horizontal, vertical, wrapText: true };
            if (height) sheetCE.getRow(rowNum).height = height;
        };

        para1(6, 'Certificated of Charted Engineer', { bold: true, horizontal: 'center', vertical: 'center' });

        sheetCE.getCell('B8').value = {
            richText: [
                { text: '1', font: { name: 'Calibri', size: FS1, bold: true } },
                { text: ' I am a Chartered Engineer having Registration No. 093577-0 dated 17.02.2006 (copy enclosed.)  My domain competence is in the field of ', font: { name: 'Calibri', size: FS1, bold: false } },
                { text: 'Production Engineering', font: { name: 'Calibri', size: FS1, bold: true } },
            ]
        };
        sheetCE.getCell('B8').alignment = { horizontal: 'left', vertical: 'center', wrapText: true };
        sheetCE.getRow(8).height = 34.5;

        sheetCE.getCell('B10').value = {
            richText: [
                { text: '2', font: { name: 'Calibri', size: FS1, bold: true } },
                { text: ` I have examined the details of requirements of parts/components imported under Authorization No. ${billNo} Date ${licDateDotted} M/s. ${cleanName} Add. ${companyAddress}I have examined accountability statement furnished by Authorization holder along with technical details thereof and requirements of part / components etc.`, font: { name: 'Calibri', size: FS1, bold: false } },
            ]
        };
        sheetCE.getCell('B10').alignment = { horizontal: 'left', vertical: 'center', wrapText: true };
        sheetCE.getRow(10).height = 86.25;

        para1(11, 'with regard to their technical description/ specification and the quantity against each item of export and having due regard to proper technical norms of consumption and after technical scrutiny of relevant process, designs and drawings of the export product, I do hereby certify that they are correct in all respects and are actually used in the manufacturing of the export product for which the EODC application made."', { vertical: 'top', height: 90.75 });

        sheetCE.getCell('B13').value = {
            richText: [
                { text: '3', font: { name: 'Calibri', size: FS1, bold: true } },
                { text: ` I hereby certify that inputs imported by M/s.${cleanName} Add ${companyAddress}`, font: { name: 'Calibri', size: FS1, bold: false } },
            ]
        };
        sheetCE.getCell('B13').alignment = { horizontal: 'left', vertical: 'center', wrapText: true };
        sheetCE.getRow(13).height = 34.5;

        para1(15, 'are actually required for manufacturing of the resultant/Exported product and as per inventory all imported inputs have been used in the resultant/ Exported products & also certify that the quantity exported, and item vise input quantity consumed as per Annexure-1 enclosed"', { height: 69 });

        para1(17, "Information furnished above if found to be incorrect or false or misleading in future, I shall be liable for penal action under the provisions of FTDR Act and rules made thereunder in addition to any other action that may be taken as per any other law, Including cancellation of membership with 'the Institution of Engineers (India)'", { height: 69 });

        para1(20, 'Place: Pune', { bold: true });
        para1(22, `Date: ${signDate}`, { bold: true });

        const sigPad = ' '.repeat(95);
        [
            `${sigPad}Signature of the Charted Engineer:`,
            `${sigPad}Name: Makarand Kulkarni`,
            `${sigPad}Telephone number: 9326769898`,
            `${sigPad}Email Address: mac_kulkarni@hotmail.com`,
        ].forEach((text, i) => {
            const rowNum = 24 + i;
            const cell = sheetCE.getCell(`B${rowNum}`);
            cell.value = text;
            cell.font = { name: 'Calibri', size: FS1, bold: true };
            cell.alignment = { horizontal: 'left', vertical: 'center', wrapText: false };
        });

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `CE_Certificate_${billNo}_${getTodaysDate()}.xlsx`);
    };

    const exportExplanatoryNotesExcel = async () => {
        if (!billNo) return;
        let workbook = new ExcelJS.Workbook();
        let sheet = workbook.addWorksheet('EXPLANATORY NOTES');
        const licData = masterSummaryData.find(
            item => String(item.lic_no || '').trim() === String(billNo || '').trim()
        );
        const companyAddress = cleanAddress(licData?.registered_address) || cleanAddress(licData?.address);
        if (!companyAddress) {
            message.error(`No licensee address is available for licence ${billNo}.`);
            return;
        }

        sheet.pageSetup = {
            paperSize: 9,
            orientation: 'portrait',
            horizontalCentered: true,
            printArea: 'B1:B30',
            margins: { left: 0.38, right: 0.34, top: 0.43, bottom: 0.56, header: 0.3, footer: 0.3 },
        };
        sheet.views = [{ state: 'normal', showGridLines: true }];
        sheet.columns = [{ width: 9.140625 }, { width: 96.140625 }, { width: 9.140625 }];
        sheet.properties.defaultRowHeight = 17.25;

        const FS = 13;
        const WRAP_W = 94;
        const cleanName = stripTradeSuffix(selectedIECName);
        const licDateDotted = licData.lic_date ? dayjs(licData.lic_date).format('DD.MM.YYYY') : '';
        const signDate = dayjs().format('DD.MM.YYYY');

        const para = (rowNum, text, opts = {}) => {
            const { bold = false, horizontal = 'left', vertical = 'middle', wrapText = true, height = null } = opts;
            const cell = sheet.getCell(`B${rowNum}`);
            cell.value = text;
            cell.font = { name: 'Calibri', size: FS, bold };
            cell.alignment = { horizontal, vertical, wrapText };
            if (height) sheet.getRow(rowNum).height = Math.max(height, getLines(text, WRAP_W) * 17.25);
        };

        para(6, 'EXPLANATORY NOTES', { bold: true, horizontal: 'center', wrapText: false });

        para(8, `Upon the request of M/s.${cleanName} Add. ${companyAddress} and verified the following set of documents pertaining to import of components against an Advance Authorization No.${billNo} Date ${licDateDotted} & utilization thereof for the manufacture & export of End Product`, { height: 86.25 });

        para(10, 'I verified the following', { wrapText: false });

        para(12, `Original Advance Authorization No.${billNo} Date ${licDateDotted} along with DES import item list duly attested by the office of the Joint Director General of Foreign Trade. Licensee address: ${companyAddress}`, { height: 51.75 });

        para(14, '2. Manufacturing process & relevant drawings', { wrapText: false });
        para(16, '3. Bill of Material for the Manufacture of an End Product.', { wrapText: false });

        para(18, `4. Verified copies of import Bills of entry along with import Debit sheet as attested by the  Customs authority for all the component imported under Advance Authorization No.${billNo} Date ${licDateDotted}`, { height: 51.75 });

        para(20, `5. After making a detailed examination of the inventory records. I hereby certify that inputs. Imported against the Advance Authorization no.${billNo} Date ${licDateDotted} are actually Required for manufacturing of the resultant product and as per inventory imported inputs, Have been actually used in the resultant product.`, { height: 69 });

        para(23, 'Place: Pune', { bold: true, wrapText: false });
        para(25, `Date: ${signDate}`, { bold: true, wrapText: false });

        // Rows 27-30: Signature block, left-aligned in Column B pushed to right with leading spaces
        const sigPad = ' '.repeat(95);
        [
            `${sigPad}Signature of the Charted Engineer:`,
            `${sigPad}Name: Makarand Kulkarni`,
            `${sigPad}Telephone number: 9326769898`,
            `${sigPad}Email Address: mac_kulkarni@hotmail.com`,
        ].forEach((text, i) => {
            const rowNum = 27 + i;
            const cell = sheet.getCell(`B${rowNum}`);
            cell.value = text;
            cell.font = { name: 'Calibri', size: FS, bold: true };
            cell.alignment = { horizontal: 'left', vertical: 'middle', wrapText: false };
        });

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `Explanatory_Notes_${billNo}_${getTodaysDate()}.xlsx`);
    };

    return (
        <Fragment>
            <style>{tableStyles}</style>
            <Row>
                <Col sm="12">
                    <Card>
                        <Card.Header className="d-flex justify-content-between">
                            <div className="header-title">
                                <h4 className="card-title">Adv Auth RDN Lic as on {formattedDate} of {selectedIECName}</h4>
                            </div>
                            <div className="d-flex gap-2 align-items-center">
                                <ColumnSelector
                                    allColumns={activeColumnConfig.allColumns}
                                    visibleKeys={activeColumnConfig.visibleKeys}
                                    setVisibleKeys={activeColumnConfig.setVisibleKeys}
                                    onReset={activeColumnConfig.resetToDefault}
                                />
                                <button
                                    className="btn btn-primary cip-btn-submit"
                                    onClick={exportToExcel}
                                    disabled={loading || error}
                                >
                                    Export to Excel
                                </button>
                                <Dropdown
                                    menu={{
                                        items: [
                                            { key: 'anf_4f', label: 'ANF-4F (Redemption Application)' },
                                            { key: 'annexure', label: 'Accountability St. (Annexure I)' },
                                            { key: 'covering_letter', label: 'Covering Letter' },
                                            { key: 'app_4h_register', label: 'Appendix 4H (Register)' },
                                            { key: 'app_4h', label: 'Appendix 4H CA' },
                                            { key: 'disclaimer', label: 'Disclaimer Cert (CENVAT)' },
                                            { key: 'ce', label: 'CE (Chartered Engineer)' },
                                            { key: 'explanatory_notes', label: 'Explanatory Notes' },
                                        ],
                                        onClick: ({ key }) => {
                                            switch (key) {
                                                case 'anf_4f':
                                                    exportAnf4FExcel();
                                                    break;
                                                case 'annexure':
                                                    exportAnnexureToExcel();
                                                    break;
                                                case 'covering_letter':
                                                    exportCoveringLetterExcel();
                                                    break;
                                                case 'app_4h_register':
                                                    exportApp4HRegisterExcel();
                                                    break;
                                                case 'app_4h':
                                                    exportApp4HExcel();
                                                    break;
                                                case 'disclaimer':
                                                    exportDisclaimerExcel();
                                                    break;
                                                case 'ce':
                                                    exportCEExcel();
                                                    break;
                                                case 'explanatory_notes':
                                                    exportExplanatoryNotesExcel();
                                                    break;
                                                default:
                                                    break;
                                            }
                                        }
                                    }}
                                    trigger={['click']}
                                >
                                    <Button className='btn btn-secondary'>
                                        Download Certificates <DownOutlined />
                                    </Button>
                                </Dropdown>
                            </div>
                        </Card.Header>
                        <Card.Body className="d-flex justify-content-start align-items-center gap-2 ps-4">
                            {userRole === 'superadmin' && (
                                <Select
                                    showSearch
                                    placeholder="Select IEC"
                                    onChange={handleIECSelection}
                                    allowClear
                                    options={IECOptions}
                                    style={{ width: '300px' }}
                                />
                            )}
                            <Select
                                showSearch
                                placeholder="Select Auth No"
                                allowClear
                                style={{ width: 250 }}
                                filterOption={(input, option) =>
                                    (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                                }
                                onChange={handleBillNoSelection}
                                options={billNoOptions}
                            />
                            <Button type="primary" className="cip-btn-submit" onClick={handleSubmit}>
                                Submit
                            </Button>
                        </Card.Body>
                        <Card.Body className="px-0 report-page">
                            {loading ? (
                                <div style={{ textAlign: 'center', padding: '20px' }}>
                                    <Spin size="large" />
                                </div>
                            ) : error ? (
                                <div style={{ textAlign: 'center', padding: '20px' }}>
                                    <p>Error loading data: {error}</p>
                                </div>
                            ) : (
                                <Tabs activeKey={activeTab} onChange={setActiveTab} style={{ padding: '0 20px' }}>
                                    <TabPane tab="Import St" key="1">
                                        <Table
                                            className="rdn-import-table"
                                            bordered
                                            columns={importStVisibility.filteredColumns}
                                            dataSource={importStData}
                                            scroll={{ x: 'max-content' }}

                                            pagination={{ showTotal: (total) => `Total ${total} records`, showSizeChanger: true }}
                                        />
                                    </TabPane>
                                    <TabPane tab="Export St" key="2">
                                        <Table
                                            className="rdn-export-table"
                                            bordered
                                            columns={exportStVisibility.filteredColumns}
                                            dataSource={exportStData}
                                            scroll={{ x: 'max-content' }}

                                            pagination={{ showTotal: (total) => `Total ${total} records`, showSizeChanger: true }}
                                        />
                                    </TabPane>
                                    <TabPane tab="SHB Wise Accountability" key="3">
                                        <Table
                                            className="rdn-shb-table"
                                            bordered
                                            columns={shbWiseVisibility.filteredColumns}
                                            dataSource={shbWiseData}
                                            scroll={{ x: 'max-content' }}

                                            pagination={{ showTotal: (total) => `Total ${total} records`, showSizeChanger: true }}
                                        />
                                    </TabPane>
                                    <TabPane tab="Adv Auth Master Summary" key="4">
                                        <Table
                                            className="rdn-master-table"
                                            bordered
                                            columns={masterSummaryVisibility.filteredColumns}
                                            dataSource={masterSummaryData}
                                            scroll={{ x: 'max-content' }}

                                            pagination={{ showTotal: (total) => `Total ${total} records`, showSizeChanger: true }}
                                        />
                                    </TabPane>
                                    <TabPane tab="Import Ledger" key="5">
                                        <Table
                                            className="rdn-import-ledger-table"
                                            bordered
                                            columns={importLedgerVisibility.filteredColumns}
                                            dataSource={importLedgerData}
                                            scroll={{ x: 'max-content' }}
                                            pagination={{ showTotal: (total) => `Total ${total} records`, showSizeChanger: true }}
                                        />
                                    </TabPane>
                                    <TabPane tab="Export Ledger" key="6">
                                        <Table
                                            className="rdn-export-ledger-table"
                                            bordered
                                            columns={exportLedgerVisibility.filteredColumns}
                                            dataSource={exportLedgerData}
                                            scroll={{ x: 'max-content' }}
                                            pagination={{ showTotal: (total) => `Total ${total} records`, showSizeChanger: true }}
                                        />
                                    </TabPane>
                                </Tabs>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Fragment>
    );
};

export default AdvanceAuthRdnLicReport;
